/* eslint-disable no-unused-vars */
/* eslint-disable no-dupe-keys */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Close as CloseIcon } from '@mui/icons-material';
import { useFolder } from '../contexts/FolderContext';
import { isNewModelFormatFolder } from '../utils/modelFormat';
import axios from 'axios';
import {
  ComposedChart,
  BarChart,
  Line,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell
} from 'recharts';
import {
  COMBO_AGGREGATE_ITEMNAME,
  isComboAggregateItem,
  aggregateDailySalesByDate,
  buildComboAggregateStrictRows,
  mgTypeForItem,
} from '../utils/comboAggregate';

const DAY_NAME_OPTIONS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ALL_DAYS_VALUE = '__ALL__';
const SLOT_DEVIATION_THRESHOLD_MINUTES = 15;

/** Parse "HH:MM" or "HH:MM:SS" to minutes from midnight. Returns null if invalid. */
const timeToMinutes = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const parts = String(timeStr).trim().split(/[:\s]+/);
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

const getMedian = (values) => {
  const nums = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (!nums.length) return null;
  const mid = Math.floor(nums.length / 2);
  if (nums.length % 2 === 1) return nums[mid];
  return (nums[mid - 1] + nums[mid]) / 2;
};

const getForecastBandFields = (row) => ({
  lower: row?.forecast_qty_lower_component_model ?? row?.forecast_qty_lower ?? row?.forecast_lower,
  upper: row?.forecast_qty_upper_component_model ?? row?.forecast_qty_upper ?? row?.forecast_upper,
});

const formatForecastRange = (lower, upper) => {
  const lo = parseFloat(lower);
  const hi = parseFloat(upper);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return '—';
  return `${lo.toFixed(0)}–${hi.toFixed(0)}`;
};

const FORECAST_BAND_CHART = {
  forecast: '#ef5350',
  bandFill: 'rgba(239, 83, 80, 0.18)',
};

/** Recharts ranged Area: dataKey must return [lower, upper] — baseLine callback is not supported per point. */
const forecastBandRangeDataKey = (entry) => {
  const lo = entry?.forecast_lower_plot;
  const hi = entry?.forecast_upper_plot;
  if (lo == null || hi == null || !Number.isFinite(lo) || !Number.isFinite(hi)) {
    return null;
  }
  return [lo, hi];
};

const getInRangeLabel = (row, actualValue = null) => {
  const actual = actualValue != null
    ? parseFloat(actualValue)
    : parseFloat(row?.actual_qty ?? 0);
  if (!Number.isFinite(actual) || actual <= 0) return '—';
  if (row?.in_range != null && row.in_range !== '') {
    const v = Number(row.in_range);
    if (Number.isFinite(v)) return v === 1 ? 'Yes' : 'No';
  }
  const { lower, upper } = getForecastBandFields(row);
  const lo = parseFloat(lower);
  const hi = parseFloat(upper);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return '—';
  return actual >= lo && actual <= hi ? 'Yes' : 'No';
};

const getInRangeChipColor = (label) => {
  if (label === 'Yes') return 'success';
  if (label === 'No') return 'error';
  return 'default';
};

const hasSoldDay = (row) => {
  const actual = parseFloat(row?.actual_qty ?? 0);
  return Number.isFinite(actual) && actual > 0;
};

const resolveInRangePct = (item, rows = []) => {
  const soldRows = rows.filter(hasSoldDay);
  if (!soldRows.length) return null;
  const hits = soldRows.filter((r) => getInRangeLabel(r) === 'Yes').length;
  return hits / soldRows.length;
};

const resolveChartBandFields = (item, itemDate, dayBandByDate) => {
  const dateKey = itemDate && !Number.isNaN(itemDate.getTime())
    ? itemDate.toISOString().split('T')[0]
    : null;
  const dayBand = dateKey && dayBandByDate ? dayBandByDate.get(dateKey) : null;
  const lo = item?.forecast_lower ?? item?.forecast_qty_lower_component_model ?? item?.forecast_qty_lower ?? dayBand?.lower;
  const hi = item?.forecast_upper ?? item?.forecast_qty_upper_component_model ?? item?.forecast_qty_upper ?? dayBand?.upper;
  return { lo, hi };
};

const getSessionBandFields = (sessionRow, dayBandByDate) => {
  const directLower = sessionRow?.session_forecast_lower ?? sessionRow?.forecast_qty_lower_component_model ?? sessionRow?.forecast_qty_lower;
  const directUpper = sessionRow?.session_forecast_upper ?? sessionRow?.forecast_qty_upper_component_model ?? sessionRow?.forecast_qty_upper;
  if (Number.isFinite(parseFloat(directLower)) && Number.isFinite(parseFloat(directUpper))) {
    return { lower: directLower, upper: directUpper };
  }
  const d = new Date(sessionRow?.date);
  if (Number.isNaN(d.getTime()) || !dayBandByDate) return { lower: null, upper: null };
  const dayEntry = dayBandByDate.get(d.toISOString().split('T')[0]);
  const dayRow = dayEntry?.row;
  if (!dayRow) return { lower: null, upper: null };
  const { lower, upper } = getForecastBandFields(dayRow);
  const dayFc = parseFloat(dayRow.forecast_qty_component_model ?? dayRow.forecast_qty ?? 0);
  const sessFc = parseFloat(sessionRow.forecast_qty_component_model ?? sessionRow.forecast_qty ?? 0);
  if (!dayFc || !Number.isFinite(parseFloat(lower)) || !Number.isFinite(parseFloat(upper))) {
    return { lower: null, upper: null };
  }
  const ratio = sessFc / dayFc;
  return {
    lower: Math.max(0, Math.round(parseFloat(lower) * ratio)),
    upper: Math.max(0, Math.round(parseFloat(upper) * ratio)),
  };
};

const getSessionInRangeLabel = (sessionRow, dayBandByDate) => {
  const actual = parseFloat(sessionRow?.actual_qty ?? 0);
  const { lower, upper } = getSessionBandFields(sessionRow, dayBandByDate);
  const lo = parseFloat(lower);
  const hi = parseFloat(upper);
  if (!Number.isFinite(actual) || actual <= 0 || !Number.isFinite(lo) || !Number.isFinite(hi)) return '—';
  return actual >= lo && actual <= hi ? 'Yes' : 'No';
};

/** Slot color by median start/end deviations. */
const getSalePeriodBarColor = ({ startMin, endMin, medianStart, medianEnd }) => {
  const isLateStart =
    Number.isFinite(startMin) &&
    Number.isFinite(medianStart) &&
    (startMin - medianStart > SLOT_DEVIATION_THRESHOLD_MINUTES);
  const isEarlyEnd =
    Number.isFinite(endMin) &&
    Number.isFinite(medianEnd) &&
    (medianEnd - endMin > SLOT_DEVIATION_THRESHOLD_MINUTES);

  if (isLateStart && isEarlyEnd) return '#e53935'; // red
  if (isLateStart) return '#fb8c00'; // orange
  if (isEarlyEnd) return '#f48fb1'; // pink
  return '#66bb6a'; // green
};

// Helper function to convert text to proper case
const toProperCase = (text) => {
  if (!text) return '';
  return String(text)
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getComponentImpactTag = (componentRow) => {
  const ratio = Number(componentRow?.avg_sale_ratio_1_over_0);
  const inRange = Number(componentRow?.ratio_1_0_in_range) === 1;

  if (!inRange || !Number.isFinite(ratio)) return 'Insignificant';
  if (ratio >= 0.9 && ratio <= 1.1) return 'Neutral';
  if (ratio > 1.1) return 'Positive';
  return 'Negative';
};

const getImpactChipColor = (impact) => {
  if (impact === 'Positive') return 'success';
  if (impact === 'Negative') return 'error';
  if (impact === 'Neutral') return 'default';
  return 'warning';
};

// Format reasons_pre into "Past Sales Related" column
const formatPastSalesReasons = (reasons_pre) => {
  if (!reasons_pre) return [];
  
  const reasons = String(reasons_pre).split(';').map(r => r.trim()).filter(r => r);
  const reasonsLower = reasons.map(r => r.toLowerCase());
  const formattedReasons = [];
  
  // Map each flag to its corresponding frontend text (case-insensitive)
  const flagMapping = {
    'insufficient_history_flag': 'Insufficient Historical Data – Model Stability Risk',
    'sparse_data_flag': 'Sparse Historical Data – Limited Observations',
    'low_scale_demand_flag': 'Low Sales Scale – Demand Too Small',
    'volatile_demand_flag': 'Volatile Demand – Unstable Sales Pattern',
    'spike_in_recent_pre_weeks_flag': 'Recent Sales Spike – Prediction Bias Risk',
    'drop_in_recent_pre_weeks_flag': 'Recent Sales Drop – Prediction Bias Risk'
  };
  
  // Check each reason and add corresponding text
  reasonsLower.forEach(reason => {
    if (flagMapping[reason]) {
      formattedReasons.push(flagMapping[reason]);
    }
  });
  
  return formattedReasons;
};

// Format reasons_post into "Current Week Anomalies" column
// If accuracy >= 90%, day-of-week spike/drop flags are filtered out
const formatCurrentWeekAnomalies = (reasons_post, accuracy = null) => {
  if (!reasons_post) return [];
  
  const reasons = String(reasons_post).split(';').map(r => r.trim()).filter(r => r);
  const reasonsLower = reasons.map(r => r.toLowerCase());
  const anomalies = [];
  
  // Calculate accuracy percentage if MAPE is provided
  // MAPE is stored as decimal (0-1), so accuracy = (1 - MAPE) * 100
  let accuracyPct = null;
  if (accuracy !== null && accuracy !== undefined) {
    const accuracyNum = typeof accuracy === 'number' ? accuracy : parseFloat(accuracy);
    if (!isNaN(accuracyNum) && isFinite(accuracyNum)) {
      // MAPE is stored as decimal (0-1), convert to accuracy percentage
      // If MAPE = 0.1 (10% error), then accuracy = (1 - 0.1) * 100 = 90%
      accuracyPct = (1 - accuracyNum) * 100;
      // Debug logging (can be removed later)
      console.log('[formatCurrentWeekAnomalies] MAPE:', accuracyNum, 'Accuracy:', accuracyPct, 'Filter day flags:', accuracyPct >= 90);
    }
  }
  
  // Check if we should filter day-of-week flags (accuracy >= 90%)
  const shouldFilterDayFlags = accuracyPct !== null && accuracyPct >= 90;
  
  // Check for DOW-specific spikes/drops (case-insensitive)
  // Only include weekdays (Monday-Friday), exclude Saturday and Sunday
  // Filter out if accuracy >= 90%
  if (!shouldFilterDayFlags) {
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const daySpikes = [];
    const dayDrops = [];
    
    dayNames.forEach(day => {
      const dayLower = day.toLowerCase();
      if (reasonsLower.some(r => r === `spike_${dayLower}`)) {
        daySpikes.push(day);
      }
      if (reasonsLower.some(r => r === `drop_${dayLower}`)) {
        dayDrops.push(day);
      }
    });

    // Add day-specific anomalies
    daySpikes.forEach(day => {
      anomalies.push(`Spike over Recent ${day}s`);
    });
    dayDrops.forEach(day => {
      anomalies.push(`Drop over Recent ${day}s`);
    });
  }
  
  // Check for operational issue flags
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  dayNames.forEach(day => {
    const dayLower = day.toLowerCase();
    // Check for "Potential Minor Issue - {Day}" → "Potential Ops Issue (Minor) - {Day}"
    if (reasons.some(r => {
      const rLower = r.toLowerCase();
      return rLower.includes(`potential minor issue - ${dayLower}`);
    })) {
      anomalies.push(`Potential Ops Issue (Minor) - ${day}`);
    }
    // Check for "Potential Ops Issue - {Day}" (without Minor) → "Potential Ops Issue - {Day}"
    else if (reasons.some(r => {
      const rLower = r.toLowerCase();
      return rLower.includes(`potential ops issue - ${dayLower}`) && !rLower.includes('minor');
    })) {
      anomalies.push(`Potential Ops Issue - ${day}`);
    }
  });
  
  // Check for overall weekly deviations (case-insensitive)
  if (reasonsLower.includes('post_spike_flag')) {
    anomalies.push('Overall Spike in Sales Across the Week');
  }
  if (reasonsLower.includes('post_drop_flag')) {
    anomalies.push('Overall Drop in Sales Across the Week');
  }
  
  return anomalies;
};

// Simple formatReasonsText for tooltips (legacy support)
const formatReasonsText = (reasons_pre, reasons_post, status) => {
  if (status === 'Green') {
    return 'No Performance Issues Detected';
  }
  
  const parts = [];
  const pastSales = formatPastSalesReasons(reasons_pre);
  const anomalies = formatCurrentWeekAnomalies(reasons_post);
  
  if (pastSales.length > 0) {
    parts.push(`Past Sales Related: ${pastSales.join('; ')}`);
    }
  if (anomalies.length > 0) {
    parts.push(`Current Week Anomalies: ${anomalies.join('; ')}`);
  }
  
  return parts.length > 0 ? parts.join('\n') : null;
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  // Check if only mean line is active
  const onlyMean = payload.length === 1 && payload[0].dataKey === 'mean';
  
  if (onlyMean) {
    // Show only mean value
    return (
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '8px',
        fontSize: '14px'
      }}>
        <p style={{ margin: 0, fontWeight: 600 }}>
          {payload[0].value.toFixed(1)}
        </p>
      </div>
    );
  }

  // For sales quantity line, show full info with reduced width
  const quantityPayload = payload.find(p => p.dataKey === 'quantity');
  const forecastPayload = payload.find(p => p.dataKey === 'forecast_plot');
  if (quantityPayload || forecastPayload) {
    const dateObj = new Date(label);
    const dateStr = dateObj.toLocaleDateString();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayOfWeek = dayNames[dateObj.getDay()];
    const description = quantityPayload?.payload?.description || '';
    const descCombo1 = quantityPayload?.payload?.description_combo1 || '';
    const descCombo2 = quantityPayload?.payload?.description_combo2 || '';
    const mgQty = quantityPayload?.payload?.mg_quantity;
    
    // Convert to proper case - capitalize every word
    const toProperCase = (str) => {
      if (!str || str === '') return '';
      return str
        .toLowerCase()
        .split(/\s+/)  // Split on any whitespace
        .map(word => {
          if (word.length === 0) return word;
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
    };
    
    const descriptionProperCase = description ? toProperCase(description) : 'Not Available';
    const dualMenus = [descCombo1, descCombo2].filter(Boolean).map(toProperCase);
    
    return (
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '8px',
        fontSize: '14px',
        maxWidth: '260px',
        whiteSpace: 'pre-line'
      }}>
        <p style={{ margin: '0 0 4px 0', fontWeight: 600 }}>
          Date: {dateStr} ({dayOfWeek})
        </p>
        {dualMenus.length > 0 ? (
          <>
            <p style={{ margin: '0 0 2px 0', fontSize: '12px', color: '#666', fontWeight: 600 }}>Menu:</p>
            {dualMenus.map((d, i) => (
              <p key={i} style={{ margin: '0 0 2px 0', fontSize: '12px', color: '#666' }}>
                {i === 0 ? 'Combo 1' : 'Combo 2'}: {d}
              </p>
            ))}
          </>
        ) : (
          <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#666' }}>
            Desc: {descriptionProperCase}
          </p>
        )}
        {quantityPayload && (
          <p style={{ margin: '0 0 4px 0' }}>
            Sales Quantity: {quantityPayload.value}
          </p>
        )}
        {mgQty != null && Number.isFinite(mgQty) && (
          <p style={{ margin: '0 0 4px 0', color: '#8e24aa' }}>
            MG: {mgQty}
          </p>
        )}
        {forecastPayload && (
          <>
            <p style={{ margin: '0 0 4px 0', color: '#757575' }}>
              Forecast: {forecastPayload.value}
            </p>
            {forecastPayload.payload?.forecast_lower_plot != null &&
              forecastPayload.payload?.forecast_upper_plot != null && (
              <p style={{ margin: '0 0 4px 0', color: '#ef5350', fontSize: '12px' }}>
                Range: {forecastPayload.payload.forecast_lower_plot}–{forecastPayload.payload.forecast_upper_plot}
              </p>
            )}
          </>
        )}
      </div>
    );
  }

  return null;
};

// Hardcoded foodcourt name mapping
const hardcodedFcMapping = {
  '6051d1f64ec6952ca5b45dba': 'CG-Ecospace 6B-BLR',
  '60f172e2d1b6e328e744cf65': 'CG-Cyber Towers-CHN',
  '61e39f4b2acc405ba5c12202': 'CG-S82-NCR-NOI',
  '62720dcd1410b643ccc5eaf0': 'CG-DV B4-BLR',
  '6344e94b2db92732f3849cd4': 'S&P Global-Prahlad Nagar-AHD',
  '651ba1552d0b1d001b6c2e53': 'Air India-Santacruz-MUM',
  '65b48ebd8d7d8f001be02f79': 'Air India-Aviation Academy-GUR',
  '66751bbe191e5a001af454d4': 'Sodexo-CG GAR-HYD',
  '66751c8e3f015e001b2ed8b4': 'Sodexo-CG-Campus-HYD',
  '66ffbf790724f300133f7c22': 'CG Technopolis- Sec 5- KOL'
};

// Restaurant mapping is now loaded from fcr_mapping.csv via API
// Removed hardcoded mapping - using API data instead
const unusedHardcodedRestMapping = {
  '60430d17af52d35e350709ef': 'Fiesta Biryani (CG -6B)',
  '6053268e4ec6952ca5b46330': 'SLV Enterprices (Veg Meal And Dosa)',
  '618df86fe211604a8da1d6b7': 'Shashi Foods (6B)',
  '626f96886ea9037357d4496d': 'SLV Enterprices (Juice Magic)',
  '6376054fc09df47d4015b813': 'SLV Enterprises (Non - Veg)',
  '63c64c1d4ebc2200177071bf': 'Biryani By Kilo - (pop-up)',
  '64b9fd63163c25001b6c371c': 'Havens Ice Cream (Popup)',
  '64d1e21a1b4f71001bae5d10': 'Biryani By Kilo (6B)',
  '64d3246b98f2fd001a215e7b': 'Haven\'s Ice Cream (CG-6B)',
  '64d38ee8596dc3001b0cdaf6': 'Chats Time (CG -6B)',
  '64eca95e15331c001857629b': 'Shahi Dum Biryani (Pop Up)',
  '64f5d700c0be82001b4c482e': 'Shahi Dum Biryani (6b - BLR)',
  '65006dc3056c32001a76de03': 'Fiesta Biryani (Pop Up)',
  '6514183defe008001bf8f65f': 'Rite Bite Max Protein (Pop up)',
  '6523b24bd3d8fd0017c076be': 'Bombay Spice Cafe',
  '652e60520c07dc00113fa720': 'The Bhojan (Pop Up)',
  '652fa0d6b5cabe001af83e20': 'Biryani 2 Home (Pop Up)',
  '654a3f3dc8f6ed00160eeef8': 'Dunkin Donuts (POP-UP)',
  '6553691b797092001bac9e5f': 'Zam Zam Food (Pop Up)',
  '656f0a65e1523400118e766a': 'Mumchies (Pop Up)',
  '656ffb04c6f93b0011ae9a94': 'ITC Master Chef Creations (Pop up)',
  '65941f0fa4f811001bd1da75': 'SGS Gundu Palav (Pop up)',
  '659e78e5b1bc68001a7d2e9b': 'Rolls N Biryani (Popup)',
  '65a613f4d3410b001b9b1231': 'Friendzy (Pop Up)',
  '65ba50aaf0aef70017a24f44': 'Dum Handi Biryani (Pop Up)',
  '65c359eba81c2b001b4b2501': 'Vimala Marketing Noodles (Pop up)',
  '65cc34a7123e31001beeffd7': 'Swizzle (Pop up)',
  '65dde438e798780017253bff': 'Burpin\'s Momos (Pop Up)',
  '65de0adf5dae430017766722': 'Loyka (Pop Up)',
  '6613e0714024df001a8fe1d3': 'Lluvia (Pop Up)',
  '6637e22d46eb59001399b6f6': 'Mango Festival ( Pop Up )',
  '663b49a75ea697001a21c1ab': 'Breakfast Biryani (Pop up)',
  '664257792fb98100139139e5': 'Peri Cookies (Pop Up)',
  '66692e0a89a512001a007580': 'Chaaunk (Pop Up)',
  '666a76efbf5017001b81f8d1': 'Savji\'s Roll and Biryani (Pop Up)',
  '66777d7588e8060019c78954': 'Krispy Kreme (Pop Up)',
  '6682862079c061001a973c56': 'Cakewala (Pop Up)',
  '668e0a434ed57800136484c4': 'WYN Brands (Pop Up)',
  '66a330998fdbec0014385316': 'SLV Enterprises (Healthy Counter).',
  '66b2264ece67000013a9fbc3': 'Taste of Home Kitchen (Pop Up)',
  '66f0f1d3b51fd8001a019785': 'Hyderabad Biryani House (Pop Up)',
  '66f409a9f47a8f001aa38e1f': 'Sakkath Millet (Pop Up)',
  '6731de0334969b001abe0565': 'Donut Dream Delight (Pop Up)',
  '674e8e114cd0c4001ae29b36': 'Go Zero (Pop Up)',
  '6753eed2ffb223001aad0614': 'Gwalia Sweets (Pop Up)',
  '6753fcc1a3c638001ab3980f': 'Brownies Basket (Pop Up)',
  '67931b8683203200139e251e': 'Bay Leaf (Pop Up)',
  '679f7e4883203200139e55ff': 'Cookieman (Pop Up)',
  '67a19e2583203200139e5861': 'Truly Rooted (Sakkhat Millet) - Pop Up',
  '67b56a2c88fa73001a76662e': 'Mandya Gowdru Donne Biryani (Pop up)',
  '67c93c742d36bd001b904e3b': 'SLV Kitchen (Pop Up)',
  '67ca7ef8935543001a59de36': 'Lassi Corner (6B BLR)',
  '67ef84e8c7668a0019f2c452': 'Kulfi and Ice cream (PopUp)',
  '681ad23cecb9c9001b2cdb88': 'Sharief Bhai Biryani (Pop Up)',
  '68276f421c429b001ab43dac': 'Fresh and Delicious (Pop Up)',
  '68593d8cb0ba0a001acdb865': 'Veda Roots (Pop Up)',
  '686255b324e74b0012bc99f6': 'Hungry Kitchen ( Pop Up)',
  '688337fa38ecce001a807cbe': 'Alvas Lassi Corner (6B BLR)',
  '68fb19b2b069010013833ec8': 'Sakkath Bites (Pop Up)',
  '690c8742368c0a001a6021d2': 'PR Biryani (Pop Up)',
  '6103aec7f337853597d65a64': 'Hot Bowl (Tuck Shop).',
  '622f1643edb3c44fb67cca33': 'Star Catering (Meal)',
  '6255440d7383114bdf5d4195': 'Hot Bowl ( Juice Counter )',
  '647885c20b19be001a884bf0': 'Baskin Robbins (Pop Up)',
  '647f2fb709f75800176f933a': 'Domino\'s (Pop Up)',
  '64c87bc1ec6f6f001b6d7ba7': 'Skandas (Meal)',
  '64df510830814500171fec01': 'Biryani (CT-CHN)',
  '6544f467e07e600011937766': 'The Waffle Shop (CT -CHN)',
  '659c05658933d00017f82731': 'Pricol (Pop up)',
  '65f52c38c591f500110eae5e': 'Rich And Krunch (Pop Up)',
  '6606707d831c9f001749aa11': 'Subathra Catering (Pop Up)',
  '6621fd5ba75b440019d4902b': 'EDF Juices (CG-Cyber Towers-CHN)',
  '6634745beb1a870013d0b166': 'Baskin Robins',
  '663e027e2fb9810013910bb7': 'Hot Plates (Meal And Snacks) - CPCT',
  '6652423427a1a900144b70e9': 'Mango Festival by Skandas(POP UP)',
  '6653349efeb1ae00135579ef': 'Fasta Pizza (Popup)',
  '66580a9f8c01370013619a83': 'Skandas - Veg Meal (CT-CHN)',
  '66581f4df01c6a001ac8aa9e': 'Skandas Non Veg Meal (CT-CHN)',
  '66658fa93e1f630019301b5d': 'Village Rose Milk (Popup).',
  '66777d7588e8060019c78954': 'Krispy Kreme (Pop Up)',
  '6694c8150dd3a300126e0d97': 'Mad Over Donut (Popup)',
  '66c854e86340710013525d30': 'Cluster Cake (Pop Up)',
  '66d9bd317cf66b001a975708': 'Adlin De Cafe (CT-CHN)',
  '67502f3a180b6d001a9c0d55': 'Soil Sweets & Savouries (Pop Up)',
  '675a7c3510d357001a93f250': 'ECR Baker (Pop Up)',
  '675c1f7effb223001aad0e6a': 'Zyn Kitchen Factory -Meals (CG Cyber Tower)',
  '678cafddf8cc1b0013aae2f7': 'Madurai Best Jigarthanda (Pop Up)',
  '67a1ccc868109c00138558a9': 'Zyn Kitchen Factory - Veg Meal',
  '67a4e64aa334d00013a10be7': 'Little North (1st Floor)',
  '67ab250ccceeca00133a1c9f': 'Farmerette Health Foods Private Limited (Pop Up)',
  '67add5299474fa0013bf2e23': 'Cookie Man (Pop Up)',
  '67b30f5e88fa73001a766347': 'Farmerette (Pop up)',
  '67b95ae3f84915001a8f9400': 'Seasons (Pop Up)',
  '67d27cc9c26e89001ad21ab7': 'Foodgrove (Pop Up)',
  '682c11c1cf20fd001a651850': 'Dream Stone Creamery (POP UP)',
  '682c76acd5e4f9001aeb2f50': 'Decadent Delights (Pop Up)',
  '684e4ea15d5f8e001a917ad5': 'Yasss Cafe Foods',
  '6863896824299e00139fb038': 'Picole -Ora Foods (Pop Up)',
  '686df6c09d240f001a74ea34': 'Oven Fresh (Pop Up)',
  '6881e7a47266d6001957aba9': 'Go Tuck (ECR Bakers)',
  '68bebc785c599d001bca155d': 'Fruit Mania (Pop Up)',
  '68f8b7e652c2470013f3e713': 'Amrudh Foods - Veg Meal',
  '690c2982f2a7eb0013daa095': 'Lemon Grove - Veg Meal',
  '690da639f1c125001acb2be5': 'Lemon Grove - Non Veg Meal',
  '690da6da8c5f81001a333485': 'Little North (Lemongrove)',
  '61e3a3cc77eb426ff13294b8': 'Arsh Foods (Go Meal & Snacks)',
  '6281f1916d5a3f5e1dbdacc6': 'Dreams Unlimited (Go Tuck)',
  '63c64c1d4ebc2200177071bf': 'Biryani By Kilo (Pop Up)',
  '63c7fa4c07977f0017c42240': 'Biryani By Kilo (POP UP)',
  '63f58c4f6c60ed001aa08ce2': 'Cake Innovation (CG - Noida)',
  '63f593b4195be600186a9172': 'Cake Innovation (Pop Up)',
  '63f5b8df195be600186a935c': 'Wow Momos (Pop Up)',
  '6453b882ac164a0017ed50f3': 'Desi Popz (CG - NOI)',
  '64620a89d2f4b20017a238b1': 'Biryani By Kilo (CG - Noida)',
  '64636f6d5aa7790017bc2b47': 'Mission Momo ( CG - Noida )',
  '646624e51275bc0016ac7114': 'Dumbo\'s (CG-NOI)',
  '646dad44a6d15d001b1a402a': 'The Kabab Story (CG-NOI)',
  '647ede0c6f65c5001721e726': 'Domino\'s (CG-Noida)',
  '647f1644918a55001b5b078e': 'The Kabab Story (Pop Up)',
  '647f2fb709f75800176f933a': 'Domino\'s (Pop Up)',
  '649adc673a721a00112ae61c': 'The Adda (CG NOI)',
  '64bf4668733b860017ef4708': 'Chaat Counter (CG-S82)',
  '64c7a7bf7a78f9001b15a760': 'Basil (CG NOI)',
  '64ca68e7be9d40001123a41d': 'Raasta Ccafe (CG NOI)',
  '64d320aeccff09001c48b8d4': 'Dumbo\'s (Pop Up)',
  '64f193217f7eab0011230f08': 'Dough and Cream (CG -NOI)',
  '650065e7275369001be8d730': 'Fomobrews (CG-NOI)',
  '6517a55b8cb450001ce8800b': 'Chaat and Panipuri (CG-NOI)',
  '6517d3b7e051f6001bb9b9fd': 'Lucknowi Rassoi (Pop Up)',
  '652f91a6feb690001008df55': 'GoChai (CG-NOIDA)',
  '652fb92f7450f3001bcf5f18': 'Dunkin Donuts (Pop Up)',
  '6540f2e4e07e600011936df2': 'Tea Cart (CG-NOI)',
  '654a3f3dc8f6ed00160eeef8': 'Dunkin Donuts (CG-NOI)',
  '655c3ad3f792cb001751cc3a': 'Samosa Kingdom (Pop Up)',
  '656f2046e1523400118e7708': ' Hotdoggin (Pop up)',
  '65cc521999aef5001bfe9700': 'Oven Story (Rebel Foods - pop up)',
  '65cda2f86e9ffd0017679c10': 'Heathy Bar & Snack Beyond Food',
  '660f88b4121fe40013dc576c': 'World Trail (Pop Up)',
  '660f8bb21138a50013406d11': 'Momos Mania ( Pop Up )',
  '664c85874f2f5600137ac0cd': 'Mango Festival By Dreams Unlimited (Pop Up)',
  '667da2f5236e16001a7a4a60': 'Go Tuck (Arsh Foods & Beverages) Tower B',
  '66838f7208d7de001af98879': 'Appe Walla (Pop up)',
  '6694c8150dd3a300126e0d97': 'Mad Over Donut (Popup)',
  '66c2ff0731f4db0013c461e1': 'Taco Bell (Pop up)',
  '66d6c3686f6088001a1f216f': 'The Belgian Waffles (Pop Up)',
  '66dae9accec9ef001a5bb54d': 'Mumbai Munchies (Popup)',
  '66de7aecf0a3b5001ad949c8': 'Mr. Moustaches Cakes (Pop Up)',
  '67175a70c9680a0013ad6122': 'The Urban Kitchen (Pop Up)',
  '67189b715903d6001a609a34': 'Chinese & Tandoori Tadka By Arsh Foods (Pop Up)',
  '672b520cd140c800134b6482': 'My Oven Recipes (Pop Up)',
  '674ace35317409001a73fbfe': 'Chatkara Cart By Dreams Unlimited(CG-NOI)',
  '6785042b83203200139e08d4': 'Bun Kool Cha (Pop Up)',
  '679f7e4883203200139e55ff': 'Cookieman (Pop Up)',
  '67a423c71ffabe00133ab3d5': 'Moye Moye (Pop Up)',
  '67b5b8a872c6a9001a804218': 'Kake Da Hotel (Pop Up)',
  '67ebb65fc42e97001a465239': 'Trius (Pop Up)',
  '68197cc0ecb9c9001b2cd9c1': 'Mitahaar (Pop Up)',
  '684a855ebd30fd001a1bd0a1': 'Cafe 9 (Go Snacks)',
  '68905ce5e50e1f001919b24d': 'Twiddles by Yuvraj Singh (Pop Up)',
  '68df9e7179844c001216f47d': 'Panchi Special (Pop Up)',
  '690b4abe8c5f81001a33317d': 'Bite Hub',
  '690c90e839a7fd00135baf6c': 'One More Bite.',
  '5f33da32f7cb2a57a708b9ee': 'Funky Belgian Waffle ( CG -B4)',
  '626fc3ec2747e63f057c5a18': 'Fresh & Rich (Juice)',
  '62765e3d14b6b767c0b31664': 'GoChai (CG-B4)',
  '62b96ba6a3b4411d5b0b508a': 'Shashi Foods',
  '63085a2195355f366f7fa0f6': 'Alvas (Chinese)',
  '630c2f4b1d03704a4b9d5774': 'Alvas (Non Veg Meal).',
  '63c64c1d4ebc2200177071bf': 'Biryani By Kilo (Pop Up)',
  '63db74c3753e39001baea166': 'Sharanya Caterers (South Meal)',
  '63f43da5d98b24001bfe1411': 'Haven\'s Ice Creams(CG-B4)',
  '6410861befdade001756eae4': 'Frosty Genie (Pop Up)',
  '647ede0c6f65c5001721e726': 'Domino\'s-POP UP',
  '649fe2d25117cc001b01e720': 'Funky Belgian Waffle (Pop Up)',
  '64a4439f929d03001b232c79': 'Healthy Bhi',
  '64b9fd63163c25001b6c371c': 'Havens Ice Cream (Popup)',
  '64ba0e66163c25001b6c3738': 'Alvas (Chatwala)',
  '64cb2d92be9d40001123a5d6': 'Yummy Tummy Fruits (CG-B4)',
  '64d0ecdba02446001b401f7e': 'Biryani By Kilo (CG-B4)',
  '64d321effa462b001bb56925': 'Royal Shahi Dum Biryani (Mimansa)',
  '64eca95e15331c001857629b': 'Shahi Dum Biryani .',
  '64ed7ce5babbec001b29a268': 'Raj Donne Biryani (CG -B4)',
  '64ede6db722d08001b658436': 'Havens Ice Cream (CG-B4)',
  '64f5521c4c59cf0011974723': 'Wow Momo\'s (CG-B4)',
  '65006dc3056c32001a76de03': 'Fiesta Biryani (Pop Up)',
  '6514183defe008001bf8f65f': 'Rite Bite Max Protein (Pop up)',
  '652a5797d4d2bd0011fbea58': 'Banashankari Donne Biryani (Pop Up)',
  '652e2a2abd191b001b3da7c0': 'Fiesta Biryani.',
  '652e55bbdb4ec900177a6248': 'Fiesta Biryani (CG-B4)',
  '652e60520c07dc00113fa720': 'The Bhojan (Pop Up)',
  '652fa0d6b5cabe001af83e20': 'Biryani 2 Home (Pop up).',
  '652fb92f7450f3001bcf5f18': 'Dunkin Donuts (Pop Up)',
  '654b766a2da0850011bdd607': 'Banashankari Donne Biryani (CG-B4)',
  '654b9184793e9e0010312e97': '5 Loaves Soda (CG-B4)',
  '656f0a65e1523400118e766a': 'Mumchies (Pop Up)',
  '656ffb04c6f93b0011ae9a94': 'ITC Master Chef Creations (Pop up)',
  '659e78e5b1bc68001a7d2e9b': 'Rolls N Biryani (Popup)',
  '65a613f4d3410b001b9b1231': 'Friendzy (Pop Up)',
  '65ba50aaf0aef70017a24f44': 'Dum Handi Biryani (Pop Up)',
  '65c359eba81c2b001b4b2501': 'Vimala Marketing Noodles (Pop up)',
  '65dde438e798780017253bff': 'Burpin\'s Momos (Pop Up)',
  '65de0adf5dae430017766722': 'Loyka (Pop Up)',
  '6613e0714024df001a8fe1d3': 'Lluvia (Pop Up)',
  '6637e22d46eb59001399b6f6': 'Mango Festival ( Pop Up )',
  '663b49a75ea697001a21c1ab': 'Breakfast Biryani (Pop up)',
  '664257792fb98100139139e5': 'Peri Cookies (Pop Up)',
  '665ea0371cd4e800135fd9ac': 'Cakelicious (Pop up)',
  '665ff759e11fec0013e24416': 'Fruit Treat (Popup)',
  '66692e0a89a512001a007580': 'Chaaunk (Pop Up)',
  '666a76efbf5017001b81f8d1': 'Savji\'s Roll and Biryani (Pop Up)',
  '66777d7588e8060019c78954': 'Krispy Kreme (Pop Up)',
  '667d3aa4f9b003001a0a3825': 'Cafe Annapoorna (CG-B4)',
  '6682862079c061001a973c56': 'Cakewala (Pop Up)',
  '66b48466824872001381c753': 'Levista (CG-B4)',
  '66d6c3686f6088001a1f216f': 'The Belgian Waffles (Pop Up)',
  '66e2bc4310878c001b3440b6': 'Tea Talk (CG-B4)',
  '66e3f3ef439ddc001aa23ae2': 'Go Meal (CG-B4)',
  '66f0f1d3b51fd8001a019785': 'Hyderabad Biryani House (Pop Up)',
  '66f409a9f47a8f001aa38e1f': 'Sakkath Millet (Pop Up)',
  '671a24a55903d6001a609b0f': 'Paradise Biryani (Pop Up)',
  '6731de0334969b001abe0565': 'Donut Dream Delight (Pop Up)',
  '674e8e114cd0c4001ae29b36': 'Go Zero (Pop Up)',
  '6753eed2ffb223001aad0614': 'Gwalia Sweets (Pop Up)',
  '6753fcc1a3c638001ab3980f': 'Brownies Basket (Pop Up)',
  '67931b8683203200139e251e': 'Bay Leaf (Pop Up)',
  '67a19e2583203200139e5861': 'Truly Rooted (Sakkhat Millet) - Pop Up',
  '67add5299474fa0013bf2e23': 'Cookie Man (Pop Up)',
  '67b56a2c88fa73001a76662e': 'Mandya Gowdru Donne Biryani (Pop up)',
  '681ad23cecb9c9001b2cdb88': 'Sharief Bhai Biryani (Pop Up)',
  '68276f421c429b001ab43dac': 'Fresh and Delicious.(Pop Up)',
  '682c11c1cf20fd001a651850': 'Dream Stone Creamery (POP UP)',
  '6835bb34b8b41b001a8dfec2': 'Jade Bake House (Pop Up)',
  '683e6688bdf3b5001a5bf537': 'IB3 Orchards (Pop Up)',
  '684133eeeb4a0a001ac3f485': 'Juicy Tale (Pop Up)',
  '6852b7f99b9a87001a77e34d': 'Mumbai Delhi Chaat Co (Pop Up)',
  '68593d8cb0ba0a001acdb865': 'Veda Roots (Pop Up)',
  '686255b324e74b0012bc99f6': 'Hungry Kitchen ( Pop Up)',
  '68771e79f05a83001a94d61a': 'TOP-Taste Of Protein (Pop Up)',
  '68baf395228b96001a58ef4a': 'Gwalia Express - North Indian Combo and Live  Counter',
  '68baf9d6a85a62001adba119': 'Gwalia Express - Chaats',
  '690c927af2a7eb0013daa2b2': 'Abi\'s Biryani Cafe (Pop Up)',
  '6344eed430ea5e3b5b617c5b': 'Go Meal (S&P Ahmedabad)',
  '63a57ff05f2e8a4ae9c021e7': 'Company Paid Events ',
  '642a5a3abe27fc00173ac498': 'Go Tuck (S&P Ahmedabad)',
  '64be7a34ce3177001a142257': 'Coolberg(S&P Ahmedabad)',
  '64d2398874bb510017cc13d8': 'The Cupcake Connection ',
  '656ecf3645b382001b148401': 'Healthy (Pop Up)',
  '65eac1b51a28be00172e7733': 'GoMeal -Jain (S&P AHD)',
  '6677795175c9ba001af969d6': 'Cake Connection (Pop up)',
  '66960ed3dd3b12001a0e23fc': 'Mad Over Donut (POP UP)',
  '66d6c3686f6088001a1f216f': 'The Belgian Waffles (Pop Up)',
  '675a9bc24cd0c4001ae2a535': 'Corn Troopers (Pop Up)',
  '67e252f0f84af0001abcafa4': 'Eatwell Caters (Pop Up)',
  '67f38bbb8dd68a001a10c175': 'Annalaksmi (Pop Up)',
  '6892084fa31a6b001be4c46c': 'The Cupcake Connection (Pop Up)',
  '690b3d5af2a7eb0013da9fe3': 'Q3 Appreciation Lunch (S&P Ahmedabad)',
  '651d3bdd0109eb001bca2fe8': 'Mad Over Donut (Air India-MUM)',
  '652cef54b2ed5d0011379b0f': 'GoTuck - (Air India-Mum)',
  '6582d95607f8de001b6b2afd': 'Bindaas Binge (Pop Up)',
  '658d3fb9fe9d76001b79f06d': 'Bindaas Binge (AirIndia-MUM)',
  '658d4c4bd9dbf9001bbe2994': 'Venture Professional Services (Air India-MUM)',
  '658d4f6cfe9d76001b79f086': 'Marriott Hospitality Service (Air India-MUM)',
  '658d52f1bc2621001b1b2770': 'Shree Tirupati Hospitality Service (Air India-MUM)',
  '658d53900ef0be001bba98ab': 'Shri Sankalpa Food and Hospitality (Air India -Mum)',
  '6822cf2a45256f001a357f39': 'Patilkaki (POP UP)',
  '6823f8becbd725001aa04dd3': 'Biryani by NODMeals. (Pop Up)',
  '68b699ed1bc7b7001aa1bdcf': 'Go Meal by Northwala',
  '63c7fa4c07977f0017c42240': 'Biryani By Kilo (POP UP)',
  '6464c3dcaf9792001b3e0d6b': 'Desi Popz (Pop Up)',
  '647f2fb709f75800176f933a': 'Domino\'s (Pop Up)',
  '652fb92f7450f3001bcf5f18': 'Dunkin Donuts (Pop Up)',
  '656f2046e1523400118e7708': ' Hotdoggin (Pop up)',
  '65b491f70b4593001c7abcab': 'Khansamas Meals (Air India -Aviation GUR)',
  '65cda2f86e9ffd0017679c10': 'Heathy Bar & Snack Beyond Food (Pop up)',
  '65f3e4244e14cb001bdf2221': 'Momo On My Plate (Pop Up)',
  '6616756fb046a9001aa273f3': 'Cake Innovation (PopUp)',
  '665ec22ec1f95c001356351d': 'Anna Biryani (Pop Up)',
  '66821df417d6c0001a1e1526': 'Charcoal Eats (Popup)',
  '66838f7208d7de001af98879': 'Appe Walla (Pop up)',
  '6694adfe192afb001367606f': 'Khansamas - Meals (Aviation - Tower C)',
  '66c2ff0731f4db0013c461e1': 'Taco Bell (Pop up)',
  '66d53be2364e5b001a7a4fda': ' Anugrahita Enterprises Private Limited (Pop Up)',
  '66de7aecf0a3b5001ad949c8': 'Mr. Moustaches Cakes (Pop Up)',
  '66e3de6ef618b8001a0dae94': 'Pizza Hut (Popup)',
  '672b520cd140c800134b6482': 'My Oven Recipes (Pop Up)',
  '678f6cfcf8cc1b0013aae888': 'Cape Komorin Cafe (Air India-Aviation Academy-GUR)',
  '67ebb65fc42e97001a465239': 'Trius (Pop Up)',
  '6815ab5b1a9ebe001ac189d6': 'Chai Era (Aviation -GUR)',
  '647f2fb709f75800176f933a': 'Domino\'s (Pop Up)',
  '652fb92f7450f3001bcf5f18': 'Dunkin Donuts (Pop Up)',
  '665eb169ee2362001365851d': 'Brown Bear (Pop Up)',
  '6675273a3f015e001b2ed8d3': 'Chai Kadak (Ground Floor)',
  '66752a7375c9ba001af9651a': 'Grab N Go (Ground Floor)',
  '6675326227db94001a09b667': 'Juices and More (Ground Floor)',
  '66753980a91532001a30ac77': 'Flavours (Healthy Counter) - Ground Floor',
  '66753d2ee8df35001a8e2297': 'Chole Bhature - Ground Floor',
  '667546e327db94001a09b6a5': 'Home Favoureats (Veg) - Ground Floor',
  '66754f0627db94001a09b6ae': 'Chai Kadak (9th Floor)',
  '66755265191e5a001af45560': 'Grab n Go (9th Floor)',
  '6675591883063d001a016919': 'Juices and More (9th Floor)',
  '66755d4c27db94001a09b6e5': 'Flavours (Chinese) - 9th Floor',
  '6675612ee8df35001a8e22ec': 'Sandwich Counter (9th Floor)',
  '667564f03f015e001b2ed94b': 'Home Favoureats (Veg) - 9th Floor',
  '6676647aa90378001a877e75': 'Dakshinam - Ground Floor',
  '6677708d29fbb3001a04509e': 'Home Favoureats (Non Veg) - 9th Floor ',
  '667771eae8df35001a8e2557': 'Home Favoureats (Non Veg) - Ground Floor',
  '667e5ba1f9b003001a0a38c2': 'Sandwich Counter (9th Floor).',
  '667e635d79c061001a972ea1': 'Dakshinam -Ground Floor.',
  '66cd56087bff86001930bc1b': 'Sai Home Foods (pop up)',
  '66eab36ca2fa79001337411e': 'Flavours- GAR -Ground Floor',
  '66eac69843226d00136af67e': 'Flavours- GAR 9th Floor',
  '66fa7dbb0c1f7b0013282f7f': 'Swirly Soiree (Pop Up)',
  '671a24a55903d6001a609b0f': 'Paradise Biryani (Pop Up)',
  '671b418ed140c800134b5829': 'The Sweets (Pop up)',
  '6735ddb9683c54001aecea00': 'Ice Stone (Pop Up)',
  '679c8c11f8cc1b0013aaf7cb': 'Chat and More - Ground Floor',
  '679dabf11ffabe00133aa976': 'Grab N Go (Ground Floor)',
  '679dad65bc4818001334989d': 'Grab n Go (9th Floor)',
  '679dc8791ffabe00133aa991': 'Chai Kadak (Ground Floor)',
  '679dd65f83203200139e5157': 'Chai Kadak (9th Floor)',
  '679df1b9cceeca001339ef93': 'Ice Stone - Ground Floor',
  '67a990a6e21de2001af663f2': 'Frankie Park (Pop- Up)',
  '67add5299474fa0013bf2e23': 'Cookie Man (Pop Up)',
  '67fcb57c612b00001adfc2b0': 'Thandai (Pop Up)',
  '681a2aa7ab0413001a3d24be': 'Pancakes Heaven (Pop Up.)',
  '6830090040da39001aad58a8': 'Chat and More - Ground Floor.',
  '683487240893bb001abc00aa': 'The Munch Box (Pop Up)',
  '68503934bd30fd001a1bd8c2': 'Parampara The Mithai Shop (Pop Up)',
  '687733c1d069c0001a2c7a9b': 'Mad Over Donut ( POP UP )',
  '68ff831e79844c00121720b4': 'Dhesi House (Pop Up)',
  '66751c8e3f015e001b2ed8b4': 'Sodexo-CG-Campus-HYD',
  '647f2fb709f75800176f933a': 'Domino\'s (Pop Up)',
  '652fb92f7450f3001bcf5f18': 'Dunkin Donuts (Pop Up)',
  '665eb169ee2362001365851d': 'Brown Bear (Pop Up)',
  '66752ba9e8df35001a8e2267': 'Chai Kadak (Second Floor)',
  '6675334a88e8060019c7845b': 'Hot Wok (Chinese) - Second Floor',
  '66753755e8df35001a8e2283': 'Grab N Go (Ground Floor)',
  '66753ea029fbb3001a044f20': 'Chai Kadak (Ground Floor).',
  '6675401b27db94001a09b686': 'Home Favoureats - Ground Floor',
  '66754812e8df35001a8e22a7': 'Juices & More - (Second Floor)',
  '6675577983063d001a016913': 'Chai Kadak(sb5th Floor)',
  '66755f4f83063d001a01692a': 'Home Favoureats (Veg) - First Floor',
  '667560c488e8060019c784dd': 'Home Favoureats (Non Veg) - First Floor',
  '66757b413f015e001b2ed975': 'Sandwich counter (Second Floor)',
  '66759201a91532001a30acf3': 'Flavours (Healthy Counter) - Second Floor',
  '6675974c191e5a001af4562a': 'Dakshinam - Second Floor',
  '66b61322e52209001364ac33': 'Hot Wok (Chinese) - Second Floor',
  '66cd56087bff86001930bc1b': 'Sai Home Foods (pop up)',
  '66eaca6277d4de0013956a35': 'Flavours- Campus - 2nd Floor',
  '66fa7dbb0c1f7b0013282f7f': 'Swirly Soiree (Pop Up)',
  '671a24a55903d6001a609b0f': 'Paradise Biryani (Pop Up)',
  '671b418ed140c800134b5829': 'The Sweets (Pop up)',
  '672dd7dcc9680a0013ad7710': 'Flavours - Healthy Counter(2nd Floor)',
  '6731e560683c54001aecd729': 'Dakshinam - Second Floor..',
  '6735ddb9683c54001aecea00': 'Ice Stone (Pop Up)',
  '67931970bc4818001334919d': 'Home Favoureats Veg (First Floor)',
  '6799a553f8cc1b0013aaf4dc': 'Chai Kadak (Ground Floor)..',
  '679dab16cceeca001339ed5c': 'Sandwich Counter (Ground Floor)..',
  '679dad43bc48180013349897': 'Juices & More - (Ground Floor)..',
  '67a990a6e21de2001af663f2': 'Frankie Park (Pop- Up)',
  '67add5299474fa0013bf2e23': 'Cookie Man (Pop Up)',
  '67fcb57c612b00001adfc2b0': 'Thandai (Pop Up)',
  '681a2aa7ab0413001a3d24be': 'Pancakes Heaven (Pop Up.)',
  '683487240893bb001abc00aa': 'The Munch Box (Pop Up)',
  '68503934bd30fd001a1bd8c2': 'Parampara The Mithai Shop (Pop Up)',
  '687733c1d069c0001a2c7a9b': 'Mad Over Donut ( POP UP )',
  '688763b4d43030001a14eaca': 'Juice & More (Ground Floor)',
  '688b095f893957001a1f5d13': 'Chai Kadak (Ground Floor)',
  '688b120e99eb19001a1b483d': 'Sandwich Counter (Ground Floor)',
  '68ff831e79844c00121720b4': 'Dhesi House (Pop Up)',
  '66fa7dc91221e30013780d6d': 'Bless Hospitality (CG-T)',
  '67c15fbded43d6001af14ec8': 'Bento Box (CG-Technopolis)',
  '67c3e3e1935543001a59d143': 'Go Tuck (CG-Technopolis)',
  '68480d58bd30fd001a1bcd77': 'Bite Hub (CG-Technopolis)',
  '684960b19b9a87001a77b8be': 'Gud Together (CG-Technopolis)'
};

function ItemDetail({ item, strictData, aggregatedData, onBack, onBackToRestaurant, source, restaurantFilters }) {
  const [itemDetailData, setItemDetailData] = useState([]);
  const [dailySalesData, setDailySalesData] = useState([]);
  const [mgData, setMgData] = useState([]);
  const isAggregateItem = isComboAggregateItem(item?.itemname);
  const [loadingSales, setLoadingSales] = useState(false);
  const [salesError, setSalesError] = useState(null);
  const [showNonZeroOnly, setShowNonZeroOnly] = useState(false);
  const [timePeriod, setTimePeriod] = useState('6months'); // Default to 6 months
  const [currentTab, setCurrentTab] = useState(0); // 0 = Date Level, 1 = Session Level
  const [sessionDetailData, setSessionDetailData] = useState([]);
  const [loadingSession, setLoadingSession] = useState(false);
  const [sessionError, setSessionError] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState('All');
  const [aggregatedSessionData, setAggregatedSessionData] = useState([]);
  const [loadingAggregatedSession, setLoadingAggregatedSession] = useState(false);
  const [fcrMapping, setFcrMapping] = useState({ fc: {}, restaurant: {} });
  const [validationSummaryData, setValidationSummaryData] = useState([]);
  const [friComponentStats, setFriComponentStats] = useState([]);
  const [loadingFriComponentStats, setLoadingFriComponentStats] = useState(false);
  const [friComponentStatsError, setFriComponentStatsError] = useState(null);
  const [dominantSession, setDominantSession] = useState(null);
  const [selectedComponentDetail, setSelectedComponentDetail] = useState(null);
  const [selectedComponentCategory, setSelectedComponentCategory] = useState('All');
  const [componentPage, setComponentPage] = useState(0);
  const { folderInfo, selectedFolder, selectedModelDate, debugMode } = useFolder();
  const [selectedDays, setSelectedDays] = useState(DAY_NAME_OPTIONS);
  const [dateRange, setDateRange] = useState([null, null]); // [startDate, endDate] for date range slider

  // Forecast window (model date +1 to +7) for rendering forecast line only in forecast period
  const forecastWindow = useMemo(() => {
    if (!selectedModelDate) return null;
    try {
      // selectedModelDate is in YYYYMMDD format, convert to Date
      const modelDateStr = selectedModelDate;
      const year = modelDateStr.substring(0, 4);
      const month = modelDateStr.substring(4, 6);
      const day = modelDateStr.substring(6, 8);
      const modelDate = new Date(`${year}-${month}-${day}`);
      
      const start = new Date(modelDate);
      start.setDate(start.getDate() + 1);
      const end = new Date(modelDate);
      end.setDate(end.getDate() + 7);
      return { start, end, modelDate };
    } catch {
      return null;
    }
  }, [selectedModelDate]);

  // Initialize date range slider with default values based on time period
  useEffect(() => {
    const timePeriodConfig = {
      '1month': { days: 28 },
      '3months': { days: 91 },
      '6months': { days: 182 },
      '1year': { days: 364 },
      '2years': { days: 730 }
    };
    const selectedConfig = timePeriodConfig[timePeriod] || timePeriodConfig['6months'];
    
    if (forecastWindow?.modelDate) {
      const modelDate = forecastWindow.modelDate;
      const defaultStart = new Date(modelDate);
      defaultStart.setDate(defaultStart.getDate() - selectedConfig.days);
      const defaultEnd = new Date(modelDate);
      defaultEnd.setDate(defaultEnd.getDate() + 7);
      setDateRange([defaultStart, defaultEnd]);
    } else if (selectedModelDate) {
      // Fallback: initialize from selectedModelDate if forecastWindow not available
      try {
        const year = selectedModelDate.substring(0, 4);
        const month = selectedModelDate.substring(4, 6);
        const day = selectedModelDate.substring(6, 8);
        const modelDate = new Date(`${year}-${month}-${day}`);
        const defaultStart = new Date(modelDate);
        defaultStart.setDate(defaultStart.getDate() - selectedConfig.days);
        const defaultEnd = new Date(modelDate);
        defaultEnd.setDate(defaultEnd.getDate() + 7);
        setDateRange([defaultStart, defaultEnd]);
      } catch (e) {
        console.error('Error initializing date range:', e);
      }
    }
  }, [forecastWindow, selectedModelDate, timePeriod]);

  const handleDayFilterChange = (event) => {
    const value = event.target.value;
    const rawValues = typeof value === 'string' ? value.split(',') : value;
    if (rawValues.includes(ALL_DAYS_VALUE)) {
      if (selectedDays.length === DAY_NAME_OPTIONS.length) {
        setSelectedDays([]);
        return;
      }
      setSelectedDays(DAY_NAME_OPTIONS);
      return;
    }
    const effectiveValues = rawValues.filter((item) => item !== ALL_DAYS_VALUE);
    if (effectiveValues.length === DAY_NAME_OPTIONS.length) {
      setSelectedDays(DAY_NAME_OPTIONS);
      return;
    }
    setSelectedDays(effectiveValues);
  };

  // Run date on/after the new-model cutoff (see utils/modelFormat.js)
  const isNewModelFormat = useMemo(
    () => isNewModelFormatFolder(selectedFolder),
    [selectedFolder]
  );

  // Model display names mapping (Existing Model removed)
  const modelDisplayNames = useMemo(() => {
    return isNewModelFormat ? (
      debugMode ? {
        'component': 'New Model (Combined)'
      } : {
        'component': 'Forecast Model'
      }
    ) : {
      'component': 'FRI-Day (Component)',
      'frid': 'FRI-Day (Enhanced)',
      'friw': 'FRI-Week'
    };
  }, [isNewModelFormat, debugMode]);

  // Safe numeric parsing for CSV values (handles '#N/A', '', null, etc.)
  const toNumber = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  const formatPercent = (v) => {
    const n = toNumber(v);
    return n === null ? '' : `${((1 - n) * 100).toFixed(1)}%`;
  };
  const formatMad = (v) => {
    const n = toNumber(v);
    return n === null ? '' : n.toFixed(1);
  };

  // Convert to proper case
  const toProperCase = (str) => {
    if (!str) return '';
    return str.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  /** Sale period data: first_order_time to last_order_time per day, filtered by selected days */
  const salePeriodTrendData = useMemo(() => {
    if (!itemDetailData || itemDetailData.length === 0) return [];
    const [startDate, endDate] = dateRange || [];
    const activeDaySet = new Set(selectedDays.length ? selectedDays : DAY_NAME_OPTIONS);
    const result = [];
    itemDetailData.forEach((row) => {
      const dateStr = (row.date || '').toString().slice(0, 10);
      if (!dateStr) return;
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return;
      const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
      if (!activeDaySet.has(dayName)) return;
      if (startDate && endDate && !Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
        if (d < startDate || d > endDate) return;
      }
      const startMin = timeToMinutes(row.first_order_time);
      const endMin = timeToMinutes(row.last_order_time);
      if (startMin == null || endMin == null || endMin <= startMin) return;
      const revenueCoverage = parseFloat(row.revenue_coverage);
      result.push({
        date: dateStr,
        offset: startMin,
        span: endMin - startMin,
        endMin,
        first_order_time: row.first_order_time || '',
        last_order_time: row.last_order_time || '',
        revenue_coverage: Number.isFinite(revenueCoverage) ? revenueCoverage : null,
        color: '#66bb6a'
      });
    });
    const sorted = result.sort((a, b) => a.date.localeCompare(b.date));
    const medianStart = getMedian(sorted.map((r) => r.offset));
    const medianEnd = getMedian(sorted.map((r) => r.endMin));

    return sorted.map((r) => ({
      ...r,
      color: getSalePeriodBarColor({
        startMin: r.offset,
        endMin: r.endMin,
        medianStart,
        medianEnd
      })
    }));
  }, [itemDetailData, selectedDays, dateRange]);

  /** Y-axis domain for Sale Period chart, derived from data */
  const salePeriodYDomain = useMemo(() => {
    if (!salePeriodTrendData.length) return [0, 1440];
    let minMin = Infinity;
    let maxMin = -Infinity;
    salePeriodTrendData.forEach(({ offset, span }) => {
      minMin = Math.min(minMin, offset);
      maxMin = Math.max(maxMin, offset + span);
    });
    if (!Number.isFinite(minMin) || !Number.isFinite(maxMin)) return [0, 1440];
    const pad = 15;
    const yMin = Math.floor(minMin / pad) * pad;
    const yMax = Math.ceil(maxMin / pad) * pad;
    return [Math.max(0, yMin), Math.min(1440, Math.max(yMax, yMin + 60))];
  }, [salePeriodTrendData]);

  /** Component Insights: list all components with category + impact tag */
  const componentInsights = useMemo(() => {
    const rows = Array.isArray(friComponentStats) ? friComponentStats : [];
    const normalizedRows = rows
      .map((r) => {
        const ratio = parseFloat(r.avg_sale_ratio_1_over_0);
        return {
          ...r,
          component_name: (r.component_name || '').toString(),
          component_category: (r.component_category || 'Uncategorized').toString(),
          avg_sale_ratio_1_over_0: Number.isFinite(ratio) ? ratio : null,
          ratio_1_0_in_range: Number(r.ratio_1_0_in_range) || 0
        };
      })
      .filter((r) => r.component_name);

    const categoryOptions = [
      'All',
      ...Array.from(new Set(normalizedRows.map((r) => r.component_category).filter(Boolean))).sort((a, b) => a.localeCompare(b))
    ];

    const filteredRows = normalizedRows
      .filter((r) => selectedComponentCategory === 'All' || r.component_category === selectedComponentCategory)
      .map((r) => ({ ...r, impact_tag: getComponentImpactTag(r) }))
      .sort((a, b) => {
        const ratioA = Number.isFinite(a.avg_sale_ratio_1_over_0) ? a.avg_sale_ratio_1_over_0 : -Infinity;
        const ratioB = Number.isFinite(b.avg_sale_ratio_1_over_0) ? b.avg_sale_ratio_1_over_0 : -Infinity;
        return ratioB - ratioA;
      });

    return {
      categoryOptions,
      filteredRows
    };
  }, [friComponentStats, selectedComponentCategory]);

  const paginatedComponentRows = useMemo(() => {
    const PAGE_SIZE = 5;
    const start = componentPage * PAGE_SIZE;
    return componentInsights.filteredRows.slice(start, start + PAGE_SIZE);
  }, [componentInsights.filteredRows, componentPage]);

  const componentPageCount = useMemo(() => {
    const PAGE_SIZE = 5;
    return Math.max(1, Math.ceil(componentInsights.filteredRows.length / PAGE_SIZE));
  }, [componentInsights.filteredRows.length]);

  useEffect(() => {
    if (isAggregateItem) {
      const aggregated = buildComboAggregateStrictRows(
        strictData,
        item.foodcourtid,
        item.restaurant
      );
      setItemDetailData(aggregated);
      return;
    }
    const filtered = strictData.filter(
      row =>
        row.foodcourtid === item.foodcourtid &&
        row.restaurant === item.restaurant &&
        row.itemname === item.itemname
    );
    setItemDetailData(filtered.sort((a, b) => new Date(a.date) - new Date(b.date)));
  }, [item, strictData, isAggregateItem]);

  // Reset UI state when item changes
  useEffect(() => {
    setCurrentTab(0); // Start with Date Level, will switch if no data
    setSelectedSessionId('All');
    setSessionDetailData([]);
    setSessionError(null);
  }, [item]);
  
  // Set default tab to first enabled tab (tab with data)
  useEffect(() => {
    const dateLevelHasData = itemDetailData && itemDetailData.length > 0;
    const sessionLevelHasData = sessionDetailData && sessionDetailData.length > 0;
    
    // Determine first enabled tab
    if (dateLevelHasData) {
      // Date Level has data, use it (or keep current if user hasn't changed)
      if (currentTab === 1 && !sessionLevelHasData) {
        setCurrentTab(0); // Switch to Date Level if Session Level has no data
      }
    } else if (sessionLevelHasData) {
      // Only Session Level has data, switch to it
      if (currentTab === 0) {
        setCurrentTab(1);
      }
    }
    // If neither has data, stay on current tab (Date Level by default)
  }, [itemDetailData, sessionDetailData]);

  // Load daily sales data for the selected FRI
  useEffect(() => {
    const loadDailySales = async () => {
      setLoadingSales(true);
      setSalesError(null);
      try {
        const folderParams = selectedFolder ? { folder: selectedFolder } : {};
        if (isAggregateItem) {
          const [r1, r2] = await Promise.all([
            axios.get('/api/daily-sales', {
              params: {
                foodcourtid: item.foodcourtid,
                restaurant: item.restaurant,
                itemname: 'Non Veg Combo 1',
                ...folderParams,
              },
            }),
            axios.get('/api/daily-sales', {
              params: {
                foodcourtid: item.foodcourtid,
                restaurant: item.restaurant,
                itemname: 'Non Veg Combo 2',
                ...folderParams,
              },
            }),
          ]);
          const merged = aggregateDailySalesByDate(r1.data || [], r2.data || []);
          setDailySalesData(merged.slice(-730));
        } else {
          const response = await axios.get('/api/daily-sales', {
            params: {
              foodcourtid: item.foodcourtid,
              restaurant: item.restaurant,
              itemname: item.itemname,
              ...folderParams,
            }
          });
          const data = response.data || [];
          setDailySalesData(data.slice(-730));
        }
      } catch (err) {
        setSalesError(err.message);
        console.error('Error loading daily sales:', err);
      } finally {
        setLoadingSales(false);
      }
    };

    if (item) {
      loadDailySales();
    }
  }, [item, selectedFolder, isAggregateItem]);

  // Load MG planned quantities (veg_lunch / non_veg_lunch)
  useEffect(() => {
    const loadMg = async () => {
      if (!item?.foodcourtid || !item?.restaurant) {
        setMgData([]);
        return;
      }
      const mgType = mgTypeForItem(item.itemname);
      if (!mgType) {
        setMgData([]);
        return;
      }
      try {
        const response = await axios.get('/api/mg-data', {
          params: {
            foodcourtid: item.foodcourtid,
            restaurant: item.restaurant,
          },
        });
        const rows = (response.data || []).filter(
          (r) => String(r.item_type || '').toLowerCase() === mgType
        );
        setMgData(rows);
      } catch (err) {
        console.warn('MG data not available:', err.message);
        setMgData([]);
      }
    };
    loadMg();
  }, [item]);

  // Load component stats for Component Insights (all ratios, categorized server-side)
  useEffect(() => {
    if (isAggregateItem) {
      setFriComponentStats([]);
      setLoadingFriComponentStats(false);
      return;
    }
    const loadFriComponentStats = async () => {
      setLoadingFriComponentStats(true);
      setFriComponentStatsError(null);
      try {
        const response = await axios.get('/api/fri-components-stats', {
          params: {
            foodcourtid: item.foodcourtid,
            restaurant: item.restaurant,
            itemname: item.itemname,
            ...(selectedFolder ? { folder: selectedFolder } : {}),
          }
        });
        setFriComponentStats(response.data || []);
      } catch (err) {
        setFriComponentStats([]);
        setFriComponentStatsError(err.message || 'Error loading component stats');
        console.error('Error loading FRI component stats:', err);
      } finally {
        setLoadingFriComponentStats(false);
      }
    };

    if (item) {
      loadFriComponentStats();
    }
  }, [item, selectedFolder, isAggregateItem]);

  useEffect(() => {
    setSelectedComponentCategory('All');
    setComponentPage(0);
  }, [item, selectedFolder]);

  useEffect(() => {
    setComponentPage(0);
  }, [selectedComponentCategory]);

  // Load dominant_session (Primary Slot) for this FRI
  useEffect(() => {
    const loadDominantSession = async () => {
      if (!item) {
        setDominantSession(null);
        return;
      }
      try {
        if (isAggregateItem) {
          const folderParams = selectedFolder ? { folder: selectedFolder } : {};
          const [r1, r2] = await Promise.all([
            axios.get('/api/dominant-session', {
              params: {
                foodcourtid: item.foodcourtid,
                restaurant: item.restaurant,
                itemname: 'Non Veg Combo 1',
                ...folderParams,
              },
            }).catch(() => ({ data: {} })),
            axios.get('/api/dominant-session', {
              params: {
                foodcourtid: item.foodcourtid,
                restaurant: item.restaurant,
                itemname: 'Non Veg Combo 2',
                ...folderParams,
              },
            }).catch(() => ({ data: {} })),
          ]);
          const s1 = (r1.data?.dominant_session ?? '').toString().trim();
          const s2 = (r2.data?.dominant_session ?? '').toString().trim();
          setDominantSession(s1 || s2 || null);
          return;
        }
        const response = await axios.get('/api/dominant-session', {
          params: {
            foodcourtid: item.foodcourtid,
            restaurant: item.restaurant,
            itemname: item.itemname,
            ...(selectedFolder ? { folder: selectedFolder } : {}),
          }
        });
        setDominantSession(response.data?.dominant_session ?? null);
      } catch {
        setDominantSession(null);
      }
    };
    loadDominantSession();
  }, [item, selectedFolder, isAggregateItem]);

  // Load validation summary data
  useEffect(() => {
    const loadValidationSummary = async () => {
      try {
        const response = await axios.get('/api/validation-summary').catch(() => ({ data: [] }));
        setValidationSummaryData(response.data || []);
      } catch (err) {
        console.error('Error loading validation summary:', err);
        setValidationSummaryData([]);
      }
    };
    loadValidationSummary();
  }, [selectedFolder]);

  // Load FCR mapping
  useEffect(() => {
    const loadFCRMapping = async () => {
      try {
        const [fcrResponse, fcResponse] = await Promise.all([
          axios.get('/api/fcr-mapping').catch(() => ({ data: [] })),
          axios.get('/api/fc-mapping').catch(() => ({ data: [] }))
        ]);
        const mapping = { fc: {}, restaurant: {} };
        
        // Load FC mapping from fc_mapping.csv (priority 1)
        if (fcResponse.data && fcResponse.data.length > 0) {
          fcResponse.data.forEach(row => {
            const fcId = (row.foodcourtid || '').toString().trim();
            const fcName = (row.foodcourtname || '').toString().trim();
            if (fcId && fcName) {
              mapping.fc[fcId] = fcName;
            }
          });
        }
        
        // Load from FCR mapping (priority 2 - for FC names not in fc_mapping.csv)
        if (fcrResponse.data && fcrResponse.data.length > 0) {
          fcrResponse.data.forEach(row => {
          const fcId = (row.foodcourtid || row.foodcourt_id || '').toString().trim();
          const fcName = (row.foodcourtname || row.foodcourt_name || '').toString().trim();
          const restId = (row.restaurant || row.restaurant_id || '').toString().trim();
          const restName = (row.restaurantname || row.restaurant_name || '').toString().trim();
            
            if (fcId && fcName && !mapping.fc[fcId]) {
            mapping.fc[fcId] = fcName;
          }
          if (restId && restName) {
            mapping.restaurant[restId] = restName;
          }
        });
        }
        
        setFcrMapping(mapping);
      } catch (err) {
        console.error('Error loading FCR mapping:', err);
      }
    };
    loadFCRMapping();
  }, []);

  // Load session-level strict data (comparison-session.csv) for the selected item
  useEffect(() => {
    if (isAggregateItem) {
      setSessionDetailData([]);
      setAggregatedSessionData([]);
      setLoadingSession(false);
      return;
    }
    const loadSessionStrict = async () => {
      setLoadingSession(true);
      setSessionError(null);
      try {
        const response = await axios.get('/api/session/strict', {
          params: {
            foodcourtid: item.foodcourtid,
            restaurant: item.restaurant,
            itemname: item.itemname
          }
        });
        const data = response.data || [];
        setSessionDetailData(data.sort((a, b) => new Date(a.date) - new Date(b.date)));
      } catch (err) {
        setSessionError(err.message);
        console.error('Error loading session strict data:', err);
      } finally {
        setLoadingSession(false);
      }
    };

    // Load session data if on Session Level tab, or if Date Level has no data (to check if Session Level has data)
    if (item && (currentTab === 1 || (currentTab === 0 && (!itemDetailData || itemDetailData.length === 0)))) {
      loadSessionStrict();
      loadAggregatedSessionData();
    }
  }, [item, currentTab, itemDetailData, isAggregateItem]);

  // Load aggregated session data
  const loadAggregatedSessionData = async () => {
    if (!item) {
      console.log('Cannot load aggregated session data - missing item', { item });
      return;
    }
    
    try {
      setLoadingAggregatedSession(true);
      console.log('Loading aggregated session data from API for item:', {
        foodcourtid: item.foodcourtid,
        restaurant: item.restaurant,
        itemname: item.itemname
      });
      
      // Use the API endpoint that already loads and caches the CSV file
      const response = await axios.get('/api/session/aggregated');
      
      // Check if response is valid
      if (!response || !response.data) {
        console.warn('No session aggregated data available');
        setAggregatedSessionData([]);
        return;
      }
      
      console.log('API response received, total rows:', response.data?.length);
      
      // Filter for current item (case-insensitive matching for itemname)
      const itemFC = (item.foodcourtid || '').toString().trim();
      const itemRest = (item.restaurant || '').toString().trim();
      const itemName = (item.itemname || '').toString().trim().toLowerCase();
      
      const data = (response.data || []).filter(row => {
        const rowFC = (row.foodcourtid || '').trim();
        const rowRest = (row.restaurant || '').trim();
        const rowItem = (row.itemname || '').trim().toLowerCase();
        
        return rowFC === itemFC && 
               rowRest === itemRest && 
               rowItem === itemName;
      });
      
      console.log('Aggregated session data filtered:', {
        totalRows: data.length,
        item: { foodcourtid: item.foodcourtid, restaurant: item.restaurant, itemname: item.itemname },
        sampleRows: data.slice(0, 3)
      });
      
      setAggregatedSessionData(data);
    } catch (err) {
      // Handle 503 (Service Unavailable) gracefully - data not loaded yet
      if (err.response?.status === 503) {
        console.warn('Session aggregated data not available yet:', err.response?.data?.error || 'Data not loaded');
        setAggregatedSessionData([]);
        return;
      }
      
      // Handle 500 (Server Error) gracefully
      if (err.response?.status === 500) {
        console.warn('Server error loading session aggregated data:', err.response?.data?.error || err.message);
        setAggregatedSessionData([]);
        return;
      }
      
      // Log other errors but don't break the UI
      console.error('Error loading aggregated session data:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setAggregatedSessionData([]);
    } finally {
      setLoadingAggregatedSession(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Green':
        return 'success';
      case 'Orange':
        return 'warning';
      case 'Red':
        return 'error';
      default:
        return 'default';
    }
  };

  // Calculate status based on MAPE and MAD (from compare_forecasts.py logic)
  const calculateStatusFromMetrics = (mape, mad) => {
    if (mape === null || mad === null || isNaN(mape) || isNaN(mad)) {
      return '';
    }
    
    const accuracyPct = mape * 100; // MAPE is already a percentage (0-1)
    
    if (accuracyPct > 90) {
      return 'Green';
    } else if (accuracyPct >= 70) {
      if (mad <= 5) {
        return 'Green';
      } else if (mad <= 15) {
        return 'Orange';
      } else {
        return 'Orange';
      }
    } else { // accuracy < 70%
      if (mad <= 5) {
        return 'Green';
      } else if (mad <= 15) {
        return 'Orange';
      } else {
        return 'Red';
      }
    }
  };

  // Calculate session-level stats from aggregated data
  // CSV columns: foodcourtid, restaurant, itemname, session_type, num_days, avg_daily_units_sold, avg_mape, avg_mad,
  // status_old, status_component_model, status_frid, status_friw, net_status_flag,
  // APE_old, APE_component_model, APE_frid, APE_friw, MAD_old, MAD_component_model, MAD_frid, MAD_friw,
  // session_start_time, session_end_time
  const sessionStats = (() => {
    console.log('Calculating sessionStats, aggregatedSessionData length:', aggregatedSessionData.length);
    if (aggregatedSessionData.length === 0) {
      console.log('No aggregated session data available');
      return { 
        sessionsSold: 0, 
        avgMAPE: null, avgMAD: null, 
        componentStatus: '', fridStatus: '', friwStatus: '',
        bestStatus: '',
        bestModelMAPE: null,
        bestModelMAD: null,
        bestModelName: ''
      };
    }
    
    // Filter sessions where avg_daily_units_sold > 0 (sessions with actual sales)
    // This counts sessions where actual quantity was > 0 for the week
    const sessionsWithSales = aggregatedSessionData.filter(row => {
      const avgUnits = parseFloat(row.avg_daily_units_sold || 0);
      return avgUnits > 0;
    });
    
    if (sessionsWithSales.length === 0) {
      return { 
        sessionsSold: 0, 
        avgMAPE: null, avgMAD: null, 
        componentStatus: '', fridStatus: '', friwStatus: '',
        bestStatus: '',
        bestModelMAPE: null,
        bestModelMAD: null,
        bestModelName: ''
      };
    }
    
    // Calculate averages across all sessions with sales for each model
    // Using avg_mape and avg_mad columns which are already aggregated per session
    let totalMAPEComponent = 0, totalMAPEOld = 0, totalMAPEFrid = 0, totalMAPEFriw = 0;
    let totalMADComponent = 0, totalMADOld = 0, totalMADFrid = 0, totalMADFriw = 0;
    let mapeCountComponent = 0, mapeCountOld = 0, mapeCountFrid = 0, mapeCountFriw = 0;
    let madCountComponent = 0, madCountOld = 0, madCountFrid = 0, madCountFriw = 0;
    
    sessionsWithSales.forEach(row => {
      // Component Model - read from APE_component_model/MAD_component_model (or fallback to avg_mape/avg_mad for backward compatibility)
      const mapeComponentStr = (row.APE_component_model || row.avg_mape_component_model || row.avg_mape || '').trim();
      const mapeComponent = mapeComponentStr === '' ? NaN : parseFloat(mapeComponentStr);
      const madComponentStr = (row.MAD_component_model || row.avg_mad_component_model || row.avg_mad || '').trim();
      const madComponent = madComponentStr === '' ? NaN : parseFloat(madComponentStr);
      if (!isNaN(mapeComponent) && isFinite(mapeComponent)) {
        totalMAPEComponent += mapeComponent;
        mapeCountComponent++;
      }
      if (!isNaN(madComponent) && isFinite(madComponent)) {
        totalMADComponent += madComponent;
        madCountComponent++;
      }
      
      // Old Model - read from APE_old/MAD_old
      const mapeOldStr = (row.APE_old || row.avg_mape_old || '').trim();
      const mapeOld = mapeOldStr === '' ? NaN : parseFloat(mapeOldStr);
      const madOldStr = (row.MAD_old || row.avg_mad_old || '').trim();
      const madOld = madOldStr === '' ? NaN : parseFloat(madOldStr);
      if (!isNaN(mapeOld) && isFinite(mapeOld)) {
        totalMAPEOld += mapeOld;
        mapeCountOld++;
      }
      if (!isNaN(madOld) && isFinite(madOld)) {
        totalMADOld += madOld;
        madCountOld++;
      }
      
      // FRID Model - read from APE_frid/MAD_frid (only for old format)
      if (!isNewModelFormat) {
        const mapeFridStr = (row.APE_frid || row.avg_mape_frid || '').trim();
        const mapeFrid = mapeFridStr === '' ? NaN : parseFloat(mapeFridStr);
        const madFridStr = (row.MAD_frid || row.avg_mad_frid || '').trim();
        const madFrid = madFridStr === '' ? NaN : parseFloat(madFridStr);
        if (!isNaN(mapeFrid) && isFinite(mapeFrid)) {
          totalMAPEFrid += mapeFrid;
          mapeCountFrid++;
        }
        if (!isNaN(madFrid) && isFinite(madFrid)) {
          totalMADFrid += madFrid;
          madCountFrid++;
        }
        
        // FRIW Model - read from APE_friw/MAD_friw
        const mapeFriwStr = (row.APE_friw || row.avg_mape_friw || '').trim();
        const mapeFriw = mapeFriwStr === '' ? NaN : parseFloat(mapeFriwStr);
        const madFriwStr = (row.MAD_friw || row.avg_mad_friw || '').trim();
        const madFriw = madFriwStr === '' ? NaN : parseFloat(madFriwStr);
        if (!isNaN(mapeFriw) && isFinite(mapeFriw)) {
          totalMAPEFriw += mapeFriw;
          mapeCountFriw++;
        }
        if (!isNaN(madFriw) && isFinite(madFriw)) {
          totalMADFriw += madFriw;
          madCountFriw++;
        }
      }
    });
    
    // Calculate average MAPE and MAD for each model
    const avgMAPEComponent = mapeCountComponent > 0 ? totalMAPEComponent / mapeCountComponent : null;
    const avgMADComponent = madCountComponent > 0 ? totalMADComponent / madCountComponent : null;
    const avgMAPEOld = mapeCountOld > 0 ? totalMAPEOld / mapeCountOld : null;
    const avgMADOld = madCountOld > 0 ? totalMADOld / madCountOld : null;
    const avgMAPEFrid = mapeCountFrid > 0 ? totalMAPEFrid / mapeCountFrid : null;
    const avgMADFrid = madCountFrid > 0 ? totalMADFrid / madCountFrid : null;
    const avgMAPEFriw = mapeCountFriw > 0 ? totalMAPEFriw / mapeCountFriw : null;
    const avgMADFriw = madCountFriw > 0 ? totalMADFriw / madCountFriw : null;
    
    // Calculate status for each model (Existing Model removed)
    const componentStatus = calculateStatusFromMetrics(avgMAPEComponent, avgMADComponent);
    const fridStatus = isNewModelFormat ? '' : calculateStatusFromMetrics(avgMAPEFrid, avgMADFrid);
    const friwStatus = isNewModelFormat ? '' : calculateStatusFromMetrics(avgMAPEFriw, avgMADFriw);
    
    // Determine best model based on lowest MAPE/MAD (Existing Model removed)
    const models = [
      { name: 'component', status: componentStatus, mape: avgMAPEComponent, mad: avgMADComponent },
      ...(isNewModelFormat ? [] : [
        { name: 'frid', status: fridStatus, mape: avgMAPEFrid, mad: avgMADFrid },
        { name: 'friw', status: friwStatus, mape: avgMAPEFriw, mad: avgMADFriw }
      ])
    ].filter(m => m.mape !== null && !isNaN(m.mape) && m.mad !== null && !isNaN(m.mad));
    
    // Find best model: lowest MAPE first, then lowest MAD if MAPE is equal
    let bestModelMAPE = null;
    let bestModelMAD = null;
    let bestModelName = '';
    
    if (models.length > 0) {
      // Sort by MAPE (lower is better), then by MAD (lower is better)
      models.sort((a, b) => {
        const mapeA = a.mape !== null && !isNaN(a.mape) ? a.mape : Infinity;
        const mapeB = b.mape !== null && !isNaN(b.mape) ? b.mape : Infinity;
        const mapeDiff = mapeA - mapeB;
        if (mapeDiff !== 0) return mapeDiff;
        // If MAPE is equal, compare MAD
        const madA = a.mad !== null && !isNaN(a.mad) ? a.mad : Infinity;
        const madB = b.mad !== null && !isNaN(b.mad) ? b.mad : Infinity;
        return madA - madB;
      });
      
      bestModelMAPE = models[0].mape;
      bestModelMAD = models[0].mad;
      bestModelName = models[0].name;
    }
    
    // Determine best model status (Existing Model removed)
    const modelsForStatus = [
      { name: 'component', status: componentStatus, mape: avgMAPEComponent },
      ...(isNewModelFormat ? [] : [
        { name: 'frid', status: fridStatus, mape: avgMAPEFrid },
        { name: 'friw', status: friwStatus, mape: avgMAPEFriw }
      ])
    ].filter(m => m.status !== '');
    
    let bestStatus = '';
    if (modelsForStatus.length > 0) {
      // Sort by status priority (Green > Orange > Red), then by MAPE (lower is better)
      const statusPriority = { 'Green': 1, 'Orange': 2, 'Red': 3 };
      modelsForStatus.sort((a, b) => {
        const statusDiff = (statusPriority[a.status] || 99) - (statusPriority[b.status] || 99);
        if (statusDiff !== 0) return statusDiff;
        const mapeA = a.mape !== null && !isNaN(a.mape) ? a.mape : Infinity;
        const mapeB = b.mape !== null && !isNaN(b.mape) ? b.mape : Infinity;
        return mapeA - mapeB;
      });
      bestStatus = modelsForStatus[0].status;
    }
    
    console.log('Session stats calculated:', {
      totalSessions: aggregatedSessionData.length,
      sessionsWithSales: sessionsWithSales.length,
      componentStatus, fridStatus, friwStatus, bestStatus,
      bestModelMAPE, bestModelMAD, bestModelName
    });
    
    // Always use New Model (component_model) for MAPE and MAD
    const calculatedAvgMAPE = avgMAPEComponent;
    const calculatedAvgMAD = avgMADComponent;
    
    return {
      sessionsSold: sessionsWithSales.length,
      avgMAPE: calculatedAvgMAPE,
      avgMAD: calculatedAvgMAD,
      componentStatus,
      fridStatus: isNewModelFormat ? '' : fridStatus,
      friwStatus: isNewModelFormat ? '' : friwStatus,
      bestStatus: isNewModelFormat ? '' : bestStatus,
      bestModelMAPE: isNewModelFormat ? null : bestModelMAPE, // MAPE of the best model (lowest MAPE/MAD)
      bestModelMAD: isNewModelFormat ? null : bestModelMAD,  // MAD of the best model (lowest MAPE/MAD)
      bestModelName: isNewModelFormat ? '' : bestModelName  // Name of the best model
    };
  })();

  // Helper function to get last week's same day value from dailySalesData
  const getLastWeekSameDay = (date) => {
    if (!dailySalesData || dailySalesData.length === 0) return null;
    
    try {
      const currentDate = new Date(date);
      const lastWeekDate = new Date(currentDate);
      lastWeekDate.setDate(lastWeekDate.getDate() - 7);
      
      // Format date as YYYY-MM-DD for matching
      const lastWeekDateStr = lastWeekDate.toISOString().split('T')[0];
      
      // Find matching date in dailySalesData
      const match = dailySalesData.find(item => {
        const itemDate = new Date(item.date);
        const itemDateStr = itemDate.toISOString().split('T')[0];
        return itemDateStr === lastWeekDateStr;
      });
      
      if (match) {
        // Try different possible column names (quantity is the primary column from daily_sales_fri)
        const value = parseFloat(match.quantity || match.Items_Qty || match.items_qty || match.qty || 0);
        // Return null if NaN or invalid
        return (isNaN(value) || !isFinite(value)) ? null : value;
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  // Calculate averages for tables
  const calculateAverages = () => {
    if (itemDetailData.length === 0) return {};
    
    const totals = {
      actual: 0,
      last_week_same_day: 0,
      old_forecast: 0,
      old_ape: 0,
      old_mad: 0,
      component_forecast: 0,
      component_ape: 0,
      component_mad: 0,
      frid_forecast: 0,
      frid_ape: 0,
      frid_mad: 0,
      friw_forecast: 0,
      friw_ape: 0,
      friw_mad: 0
    };
    
    let lastWeekCount = 0;
    
    itemDetailData.forEach(row => {
      totals.actual += parseFloat(row.actual_qty) || 0;
      
      const lastWeekValue = getLastWeekSameDay(row.date);
      if (lastWeekValue !== null && lastWeekValue !== undefined && !isNaN(lastWeekValue)) {
        totals.last_week_same_day += lastWeekValue;
        lastWeekCount++;
      }
      
      totals.old_forecast += parseFloat(row.forecast_qty_old) || 0;
      totals.old_ape += parseFloat(row.APE_old) || 0;
      totals.old_mad += parseFloat(row.MAD_old) || 0;
      totals.component_forecast += parseFloat(row.forecast_qty_component_model) || 0;
      totals.component_ape += parseFloat(row.APE_component_model) || 0;
      totals.component_mad += parseFloat(row.MAD_component_model) || 0;
      totals.frid_forecast += parseFloat(row.forecast_qty_frid) || 0;
      totals.frid_ape += parseFloat(row.APE_frid) || 0;
      totals.frid_mad += parseFloat(row.MAD_frid) || 0;
      totals.friw_forecast += parseFloat(row.forecast_qty_friw) || 0;
      totals.friw_ape += parseFloat(row.APE_friw) || 0;
      totals.friw_mad += parseFloat(row.MAD_friw) || 0;
    });
    
    const count = itemDetailData.length;
    return {
      actual: totals.actual / count,
      last_week_same_day: lastWeekCount > 0 ? totals.last_week_same_day / lastWeekCount : null,
      old_forecast: totals.old_forecast / count,
      old_ape: totals.old_ape / count,
      old_mad: totals.old_mad / count,
      component_forecast: totals.component_forecast / count,
      component_ape: totals.component_ape / count,
      component_mad: totals.component_mad / count,
      frid_forecast: totals.frid_forecast / count,
      frid_ape: totals.frid_ape / count,
      frid_mad: totals.frid_mad / count,
      friw_forecast: totals.friw_forecast / count,
      friw_ape: totals.friw_ape / count,
      friw_mad: totals.friw_mad / count
    };
  };

  const averages = calculateAverages();

  const inRangePct = useMemo(
    () => resolveInRangePct(item, itemDetailData),
    [item, itemDetailData]
  );

  const dayBandByDate = useMemo(() => {
    const m = new Map();
    itemDetailData.forEach((row) => {
      const d = new Date(row.date);
      if (Number.isNaN(d.getTime())) return;
      const key = d.toISOString().split('T')[0];
      const { lower, upper } = getForecastBandFields(row);
      m.set(key, { lower, upper, row });
    });
    return m;
  }, [itemDetailData]);

  // Calculate avg MAPE and avg MAD from the 4 models (All Models) and Best Model
  const calculateAvgMetrics = () => {
    if (itemDetailData.length === 0) return {
      avgMAPE: null,
      avgMAD: null,
      bestMAPE: null,
      bestMAD: null
    };
    
    let totalAPE = 0;
    let totalMAD = 0;

    // Track totals for each model to calculate overall averages
    let totalAPE_old = 0;
    let totalAPE_component = 0;
    let totalAPE_frid = 0;
    let totalAPE_friw = 0;
    let totalMAD_old = 0;
    let totalMAD_component = 0;
    let totalMAD_frid = 0;
    let totalMAD_friw = 0;

    let countAPE_old = 0;
    let countAPE_component = 0;
    let countAPE_frid = 0;
    let countAPE_friw = 0;
    let countMAD_old = 0;
    let countMAD_component = 0;
    let countMAD_frid = 0;
    let countMAD_friw = 0;

    let count = 0;
    
    itemDetailData.forEach(row => {
      const apeOld = parseFloat(row.APE_old) || 0;
      const apeComponent = parseFloat(row.APE_component_model) || 0;
      const apeFrid = parseFloat(row.APE_frid) || 0;
      const apeFriw = parseFloat(row.APE_friw) || 0;
      const madOld = parseFloat(row.MAD_old) || 0;
      const madComponent = parseFloat(row.MAD_component_model) || 0;
      const madFrid = parseFloat(row.MAD_frid) || 0;
      const madFriw = parseFloat(row.MAD_friw) || 0;
      
      // Always use New Model (component_model) for MAPE and MAD
          if (!isNaN(apeComponent) && apeComponent >= 0) {
            totalAPE += apeComponent;
          }
          if (!isNaN(madComponent) && madComponent >= 0) {
            totalMAD += madComponent;
      }

      // Accumulate totals for each model (only count valid values)
      if (!isNaN(apeOld) && apeOld >= 0) {
        totalAPE_old += apeOld;
        countAPE_old++;
      }
      if (!isNaN(apeComponent) && apeComponent >= 0) {
        totalAPE_component += apeComponent;
        countAPE_component++;
      }
      if (!isNaN(apeFrid) && apeFrid >= 0) {
        totalAPE_frid += apeFrid;
        countAPE_frid++;
      }
      if (!isNaN(apeFriw) && apeFriw >= 0) {
        totalAPE_friw += apeFriw;
        countAPE_friw++;
      }

      if (!isNaN(madOld) && madOld >= 0) {
        totalMAD_old += madOld;
        countMAD_old++;
      }
      if (!isNaN(madComponent) && madComponent >= 0) {
        totalMAD_component += madComponent;
        countMAD_component++;
      }
      if (!isNaN(madFrid) && madFrid >= 0) {
        totalMAD_frid += madFrid;
        countMAD_frid++;
      }
      if (!isNaN(madFriw) && madFriw >= 0) {
        totalMAD_friw += madFriw;
        countMAD_friw++;
      }

      count++;
    });

    // Always use component model count for avgMAPE/avgMAD
    const effectiveCount = countAPE_component > 0 ? countAPE_component : count;

    // Calculate overall averages for each model
    const overallAPE_old = countAPE_old > 0 ? totalAPE_old / countAPE_old : Infinity;
    const overallAPE_component = countAPE_component > 0 ? totalAPE_component / countAPE_component : Infinity;
    const overallAPE_frid = countAPE_frid > 0 ? totalAPE_frid / countAPE_frid : Infinity;
    const overallAPE_friw = countAPE_friw > 0 ? totalAPE_friw / countAPE_friw : Infinity;

    const overallMAD_old = countMAD_old > 0 ? totalMAD_old / countMAD_old : Infinity;
    const overallMAD_component = countMAD_component > 0 ? totalMAD_component / countMAD_component : Infinity;
    const overallMAD_frid = countMAD_frid > 0 ? totalMAD_frid / countMAD_frid : Infinity;
    const overallMAD_friw = countMAD_friw > 0 ? totalMAD_friw / countMAD_friw : Infinity;

    // Find the best model (lowest overall MAPE/MAD) - Existing Model removed
    const apeValues = [
      { value: overallAPE_component, name: 'component' },
      ...(isNewModelFormat ? [] : [
        { value: overallAPE_frid, name: 'frid' },
        { value: overallAPE_friw, name: 'friw' }
      ])
    ].filter(v => v.value !== Infinity);

    const madValues = [
      { value: overallMAD_component, name: 'component' },
      ...(isNewModelFormat ? [] : [
        { value: overallMAD_frid, name: 'frid' },
        { value: overallMAD_friw, name: 'friw' }
      ])
    ].filter(v => v.value !== Infinity);

    const bestAPE = apeValues.length > 0 ? Math.min(...apeValues.map(v => v.value)) : null;
    const bestMAD = madValues.length > 0 ? Math.min(...madValues.map(v => v.value)) : null;

    // Find which model has the best overall MAPE and MAD
    const bestAPEModel = apeValues.length > 0
      ? apeValues.find(v => v.value === bestAPE)?.name || null
      : null;
    const bestMADModel = madValues.length > 0
      ? madValues.find(v => v.value === bestMAD)?.name || null
      : null;

    // Map model names to display names (Existing Model removed)
    const modelDisplayNames = isNewModelFormat ? (
      debugMode ? {
        'component': 'New Model (Combined)'
      } : {
        'component': 'Forecast Model'
      }
    ) : {
      'component': 'FRI-Day (Component)',
      'frid': 'FRI-Day (Enhanced)',
      'friw': 'FRI-Week'
    };
    
    return {
      avgMAPE: effectiveCount > 0 ? totalAPE / effectiveCount : null,
      avgMAD: effectiveCount > 0 ? totalMAD / effectiveCount : null,
      bestMAPE: bestAPE,
      bestMAD: bestMAD,
      bestAPEModel: bestAPEModel ? modelDisplayNames[bestAPEModel] : null,
      bestMADModel: bestMADModel ? modelDisplayNames[bestMADModel] : null
    };
  };

  const { avgMAPE, avgMAD, bestMAPE, bestMAD, bestAPEModel, bestMADModel } = calculateAvgMetrics();

  // Session helpers (comparison-session.csv)
  const uniqueSessions = (() => {
    const map = new Map();
    sessionDetailData.forEach(r => {
      // CSV uses session_id (with underscore), but also support sessionid for backward compatibility
      const id = r.session_id || r.sessionid || `${r.session_type || ''}_${r.session_start_time || ''}_${r.session_end_time || ''}`;
      
      // Check if this row has actual data (actual > 0)
      // Disable sessions where forecast is available but actual is not
      const actualQty = toNumber(r.actual_qty) || 0;
      const forecastOld = toNumber(r.forecast_qty_old) || 0;
      const forecastComponent = toNumber(r.forecast_qty_component_model) || 0;
      const forecastFrid = toNumber(r.forecast_qty_frid) || 0;
      const forecastFriw = toNumber(r.forecast_qty_friw) || 0;
      
      const hasActual = actualQty > 0;
      const hasForecast = forecastOld > 0 || forecastComponent > 0 || forecastFrid > 0 || forecastFriw > 0;
      
      // Session has data only if it has actual sales (not just forecasts)
      const rowHasData = hasActual;
      
      if (!map.has(id)) {
        const label = `${toProperCase(r.session_type || '')} (${r.session_start_time || ''}-${r.session_end_time || ''})`;
        map.set(id, { id, label, hasData: rowHasData });
      } else {
        // Update hasData if this row has actual data (session has data if ANY row has actual data)
        const existing = map.get(id);
        if (rowHasData) {
          existing.hasData = true;
        }
      }
    });
    return Array.from(map.values());
  })();

  // Default to the first session with data (skip greyed-out sessions)
  useEffect(() => {
    if (currentTab !== 1) return;
    if (selectedSessionId && selectedSessionId !== 'All') return;
    if (uniqueSessions.length === 0) return;
    
    // Find first session with data
    const firstSessionWithData = uniqueSessions.find(s => s.hasData !== false);
    if (firstSessionWithData) {
      setSelectedSessionId(firstSessionWithData.id);
    } else if (uniqueSessions.length > 0) {
      // Fallback to first session if none have data
      setSelectedSessionId(uniqueSessions[0].id);
    }
  }, [currentTab, selectedSessionId, uniqueSessions]);

  const filteredSessionRows = (selectedSessionId
    ? sessionDetailData.filter(r => {
        // CSV uses session_id (with underscore), but also support sessionid for backward compatibility
        const sessionId = r.session_id || r.sessionid || '';
        return sessionId === selectedSessionId;
      })
    : sessionDetailData
  ).filter(r => {
    // Only show dates where actual is not 0
    const actualQty = parseFloat(r.actual_qty || 0);
    return actualQty > 0;
  });

  const sessionInRangePct = useMemo(() => {
    if (!filteredSessionRows.length) return null;
    const hits = filteredSessionRows.filter((r) => getSessionInRangeLabel(r, dayBandByDate) === 'Yes').length;
    return hits / filteredSessionRows.length;
  }, [filteredSessionRows, dayBandByDate]);

  const sessionAverages = (() => {
    if (filteredSessionRows.length === 0) {
      return { 
        actual: null, 
        forecast_component: null, forecast_old: null, forecast_frid: null, forecast_friw: null,
        ape_component: null, ape_old: null, ape_frid: null, ape_friw: null,
        mad_component: null, mad_old: null, mad_frid: null, mad_friw: null
      };
    }
    let totalActual = 0;
    let totalForecastComponent = 0, totalForecastOld = 0, totalForecastFrid = 0, totalForecastFriw = 0;
    let totalMADComponent = 0, totalMADOld = 0, totalMADFrid = 0, totalMADFriw = 0;
    let totalAPEComponent = 0, totalAPEOld = 0, totalAPEFrid = 0, totalAPEFriw = 0;
    let apeCountComponent = 0, apeCountOld = 0, apeCountFrid = 0, apeCountFriw = 0;
    let count = 0;

    filteredSessionRows.forEach(r => {
      totalActual += toNumber(r.actual_qty) || 0;
      
      // Component Model
      totalForecastComponent += toNumber(r.forecast_qty_component_model) || 0;
      const madComponent = toNumber(r.MAD_component_model);
      if (madComponent !== null) totalMADComponent += madComponent;
      const apeComponent = toNumber(r.APE_component_model);
      if (apeComponent !== null) {
        totalAPEComponent += apeComponent;
        apeCountComponent++;
      }
      
      // Existing Model (Old)
      totalForecastOld += toNumber(r.forecast_qty_old) || 0;
      const madOld = toNumber(r.MAD_old);
      if (madOld !== null) totalMADOld += madOld;
      const apeOld = toNumber(r.APE_old);
      if (apeOld !== null) {
        totalAPEOld += apeOld;
        apeCountOld++;
      }
      
      // FRI-Day Enhanced (FRID) - only for old format
      if (!isNewModelFormat) {
        totalForecastFrid += toNumber(r.forecast_qty_frid) || 0;
        const madFrid = toNumber(r.MAD_frid);
        if (madFrid !== null) totalMADFrid += madFrid;
        const apeFrid = toNumber(r.APE_frid);
        if (apeFrid !== null) {
          totalAPEFrid += apeFrid;
          apeCountFrid++;
        }
        
        // FRI-Week (FRIW)
        totalForecastFriw += toNumber(r.forecast_qty_friw) || 0;
        const madFriw = toNumber(r.MAD_friw);
        if (madFriw !== null) totalMADFriw += madFriw;
        const apeFriw = toNumber(r.APE_friw);
        if (apeFriw !== null) {
          totalAPEFriw += apeFriw;
          apeCountFriw++;
        }
      }
      
      count++;
    });

    return {
      actual: count > 0 ? totalActual / count : null,
      forecast_component: count > 0 ? totalForecastComponent / count : null,
      forecast_old: count > 0 ? totalForecastOld / count : null,
      forecast_frid: count > 0 ? totalForecastFrid / count : null,
      forecast_friw: count > 0 ? totalForecastFriw / count : null,
      ape_component: apeCountComponent > 0 ? totalAPEComponent / apeCountComponent : null,
      ape_old: apeCountOld > 0 ? totalAPEOld / apeCountOld : null,
      ape_frid: apeCountFrid > 0 ? totalAPEFrid / apeCountFrid : null,
      ape_friw: apeCountFriw > 0 ? totalAPEFriw / apeCountFriw : null,
      mad_component: count > 0 ? totalMADComponent / count : null,
      mad_old: count > 0 ? totalMADOld / count : null,
      mad_frid: count > 0 ? totalMADFrid / count : null,
      mad_friw: count > 0 ? totalMADFriw / count : null
    };
  })();

  // Also calculate statuses from filtered session rows (Existing Model removed)
  const sessionModelStatuses = (() => {
    if (filteredSessionRows.length === 0) {
      return { componentStatus: '', fridStatus: '', friwStatus: '', bestStatus: '' };
    }
    
    const componentStatus = calculateStatusFromMetrics(sessionAverages.ape_component, sessionAverages.mad_component);
    const fridStatus = isNewModelFormat ? '' : calculateStatusFromMetrics(sessionAverages.ape_frid, sessionAverages.mad_frid);
    const friwStatus = isNewModelFormat ? '' : calculateStatusFromMetrics(sessionAverages.ape_friw, sessionAverages.mad_friw);
    
    const models = [
      { name: 'component', status: componentStatus, mape: sessionAverages.ape_component },
      ...(isNewModelFormat ? [] : [
        { name: 'frid', status: fridStatus, mape: sessionAverages.ape_frid },
        { name: 'friw', status: friwStatus, mape: sessionAverages.ape_friw }
      ])
    ].filter(m => m.status !== '');
    
    let bestStatus = '';
    if (models.length > 0) {
      const statusPriority = { 'Green': 1, 'Orange': 2, 'Red': 3 };
      models.sort((a, b) => {
        const statusDiff = (statusPriority[a.status] || 99) - (statusPriority[b.status] || 99);
        if (statusDiff !== 0) return statusDiff;
        const mapeA = a.mape !== null && !isNaN(a.mape) ? a.mape : Infinity;
        const mapeB = b.mape !== null && !isNaN(b.mape) ? b.mape : Infinity;
        return mapeA - mapeB;
      });
      bestStatus = models[0].status;
    }
    
    return { 
      componentStatus, 
      fridStatus: isNewModelFormat ? '' : fridStatus, 
      friwStatus: isNewModelFormat ? '' : friwStatus, 
      bestStatus: isNewModelFormat ? '' : bestStatus 
    };
  })();

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'flex-end' }}>
        {source === 'restaurantlevel' && onBackToRestaurant && restaurantFilters && (
          // Coming from Restaurant Forecasts - show only Back to Restaurant Forecasts
          <Button 
            onClick={() => onBackToRestaurant(restaurantFilters)} 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
          >
            Back to Restaurant Forecasts
          </Button>
        )}
        {source !== 'restaurantlevel' && onBack && (
          // Coming from Item List (DeepDive) - show only Back to Item List
          <Button onClick={onBack} variant="outlined" startIcon={<ArrowBackIcon />}>
            Back to Item List
          </Button>
        )}
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            color: 'primary.main',
            mb: 1
          }}
        >
          Item Name : {toProperCase(item.itemname)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            <strong>Food Court:</strong> {fcrMapping.fc[item.foodcourtid] || hardcodedFcMapping[item.foodcourtid] || item.foodcourtid}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            <strong>Restaurant:</strong> {fcrMapping.restaurant[item.restaurant] || item.restaurant}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
            <strong>Model Publish Date:</strong> {folderInfo?.publishDate || '12 Dec'}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
            <strong>Forecast Week:</strong> {folderInfo?.forecastWeek || 'Nov 16-22'}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Tabs (per item) */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}>
          <Tab label="Date Level" />
          <Tab label="Session Level" />
        </Tabs>
      </Box>

      {/* Date Level (keep as-is) */}
      {currentTab === 0 && (
        <>
      {/* Day Level */}
      <Grid
        container
        spacing={2}
        sx={{ mb: 4, flexWrap: { xs: 'wrap', sm: 'wrap', md: 'nowrap' } }}
      >
        <Grid item xs={12} sm={isNewModelFormat ? 6 : 4} md={isNewModelFormat ? 3 : 2} sx={{ flex: { md: isNewModelFormat ? '0 0 25%' : '0 0 20%' }, maxWidth: { md: isNewModelFormat ? '25%' : '20%' } }}>
          <Card
            elevation={1}
            sx={{
              bgcolor: 'rgba(25, 118, 210, 0.08)',
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              height: 120,
              minHeight: 120,
              display: 'flex'
            }}
          >
            <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <Typography color="text.secondary" gutterBottom variant="body2">
                #Days Sold
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {item.num_days || 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={isNewModelFormat ? 6 : 4} md={isNewModelFormat ? 3 : 2} sx={{ flex: { md: isNewModelFormat ? '0 0 25%' : '0 0 20%' }, maxWidth: { md: isNewModelFormat ? '25%' : '20%' } }}>
          <Card
            elevation={1}
            sx={{
              bgcolor: 'rgba(76, 175, 80, 0.08)',
              borderLeft: '4px solid',
              borderColor: 'success.main',
              height: 120,
              minHeight: 120,
              display: 'flex'
            }}
          >
            <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <Typography color="text.secondary" gutterBottom variant="body2">
                {debugMode ? 'Avg Predicted Units' : 'Avg Daily Units Sold'}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                {debugMode
                  ? (averages?.component_forecast != null && Number.isFinite(averages.component_forecast)
                      ? averages.component_forecast.toFixed(0)
                      : 'N/A')
                  : (item.avg_daily_units_sold
                      ? parseFloat(item.avg_daily_units_sold).toFixed(0)
                      : 'N/A')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {isNewModelFormat && (
          <Grid item xs={12} sm={6} md={3} sx={{ flex: { md: '0 0 25%' }, maxWidth: { md: '25%' } }}>
            <Card
              elevation={1}
              sx={{
                bgcolor: 'rgba(239, 83, 80, 0.08)',
                borderLeft: '4px solid',
                borderColor: 'error.main',
                height: 120,
                minHeight: 120,
                display: 'flex'
              }}
            >
              <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  In Range (±1σ)
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'error.main' }}>
                  {inRangePct != null && Number.isFinite(inRangePct)
                    ? `${(inRangePct * 100).toFixed(0)}%`
                    : 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Days with sales inside forecast band
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Date-wise Forecast Comparison
        </Typography>
        <Grid container spacing={2}>
          {isNewModelFormat ? (
            <Grid item xs={12}>
              <Card elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 1.5, bgcolor: 'rgba(156, 39, 176, 0.1)', borderBottom: '1px solid', borderColor: 'divider', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
                    {debugMode ? 'New Model (Combined)' : 'Forecast vs Actual'}
                  </Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 400, overflow: 'auto', flex: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Actual</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Forecast</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Range (Lower–Upper)</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Accuracy%</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Abs. Error</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="center">In Range</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemDetailData.map((row, idx) => {
                        const { lower, upper } = getForecastBandFields(row);
                        const inRangeLabel = getInRangeLabel(row);
                        return (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontSize: '0.7rem' }}>{new Date(row.date).toLocaleDateString()}</TableCell>
                            <TableCell sx={{ fontSize: '0.7rem' }} align="right">{parseFloat(row.actual_qty || 0).toFixed(0)}</TableCell>
                            <TableCell sx={{ fontSize: '0.7rem' }} align="right">{parseFloat(row.forecast_qty_component_model || row.forecast_qty || 0).toFixed(0)}</TableCell>
                            <TableCell sx={{ fontSize: '0.7rem' }} align="right">{formatForecastRange(lower, upper)}</TableCell>
                            <TableCell sx={{ fontSize: '0.7rem' }} align="right">{formatPercent(row.APE_component_model)}</TableCell>
                            <TableCell sx={{ fontSize: '0.7rem' }} align="right">{formatMad(row.MAD_component_model)}</TableCell>
                            <TableCell sx={{ fontSize: '0.7rem' }} align="center">
                              <Chip
                                label={inRangeLabel}
                                size="small"
                                color={getInRangeChipColor(inRangeLabel)}
                                variant={inRangeLabel === '—' ? 'outlined' : 'filled'}
                                sx={{ fontSize: '0.65rem', height: 20, minWidth: 44 }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Average</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                          {(averages && averages.actual != null && Number.isFinite(averages.actual)) ? averages.actual.toFixed(1) : 'N/A'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                          {averages.component_forecast ? averages.component_forecast.toFixed(1) : 'N/A'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>—</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                          {Number.isFinite(averages.component_ape) ? `${((1 - averages.component_ape) * 100).toFixed(1)}%` : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                          {Number.isFinite(averages.component_mad) ? averages.component_mad.toFixed(1) : '—'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                          {inRangePct != null && Number.isFinite(inRangePct)
                            ? `${(inRangePct * 100).toFixed(0)}%`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          ) : (
          <>
          {/* Actual Data Table */}
          <Grid item xs={12} md={12}>
            <Card elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.08)', borderBottom: '1px solid', borderColor: 'divider', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
                  Actual Data
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 400, overflow: 'auto', flex: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Actual Quantity Sold</TableCell>
                      {isNewModelFormat && (
                        <>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Range</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="center">In Range</TableCell>
                        </>
                      )}
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Last Week (Same Day)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemDetailData.map((row, idx) => {
                      const lastWeekValue = getLastWeekSameDay(row.date);
                      const { lower, upper } = getForecastBandFields(row);
                      const inRangeLabel = getInRangeLabel(row);
                      return (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontSize: '0.7rem' }}>{new Date(row.date).toLocaleDateString()}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }} align="right">{parseFloat(row.actual_qty || 0).toFixed(0)}</TableCell>
                        {isNewModelFormat && (
                          <>
                            <TableCell sx={{ fontSize: '0.7rem' }} align="right">{formatForecastRange(lower, upper)}</TableCell>
                            <TableCell sx={{ fontSize: '0.7rem' }} align="center">
                              <Chip
                                label={inRangeLabel}
                                size="small"
                                color={getInRangeChipColor(inRangeLabel)}
                                variant={inRangeLabel === '—' ? 'outlined' : 'filled'}
                                sx={{ fontSize: '0.65rem', height: 20, minWidth: 44 }}
                              />
                            </TableCell>
                          </>
                        )}
                          <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                            {(lastWeekValue !== null && lastWeekValue !== undefined && !isNaN(lastWeekValue)) ? lastWeekValue.toFixed(0) : 'N/A'}
                          </TableCell>
                      </TableRow>
                      );
                    })}
                    <TableRow sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Average</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        {(averages && averages.actual !== null && averages.actual !== undefined && !isNaN(averages.actual)) ? averages.actual.toFixed(1) : 'N/A'}
                      </TableCell>
                      {isNewModelFormat && (
                        <>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>—</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                            {inRangePct != null && Number.isFinite(inRangePct)
                              ? `${(inRangePct * 100).toFixed(0)}%`
                              : '—'}
                          </TableCell>
                        </>
                      )}
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        {(averages && averages.last_week_same_day !== null && averages.last_week_same_day !== undefined && !isNaN(averages.last_week_same_day)) ? averages.last_week_same_day.toFixed(1) : 'N/A'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          {/* Component Model Table */}
          <Grid item xs={12} sm={6} md={isNewModelFormat ? 6 : 3}>
            <Card elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.1)', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {isNewModelFormat ? (debugMode ? 'New Model (Combined)' : 'Forecast Model') : 'FRI-Day (Component)'}
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 400, overflow: 'auto', flex: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Forecast</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Range</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="center">In Range</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Accuracy%</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Abs. Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemDetailData.map((row, idx) => {
                      const { lower, upper } = getForecastBandFields(row);
                      const inRangeLabel = getInRangeLabel(row);
                      return (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontSize: '0.7rem' }}>{new Date(row.date).toLocaleDateString()}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }} align="right">{parseFloat(row.forecast_qty_component_model || row.forecast_qty || 0).toFixed(0)}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }} align="right">{formatForecastRange(lower, upper)}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }} align="center">
                          <Chip
                            label={inRangeLabel}
                            size="small"
                            color={getInRangeChipColor(inRangeLabel)}
                            variant={inRangeLabel === '—' ? 'outlined' : 'filled'}
                            sx={{ fontSize: '0.65rem', height: 20, minWidth: 44 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                          {formatPercent(row.APE_component_model)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                          {formatMad(row.MAD_component_model)}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    <TableRow sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Average</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        {averages.component_forecast ? averages.component_forecast.toFixed(1) : 'N/A'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>—</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        {inRangePct != null && Number.isFinite(inRangePct)
                          ? `${(inRangePct * 100).toFixed(0)}%`
                          : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        {Number.isFinite(averages.component_ape) ? `${((1 - averages.component_ape) * 100).toFixed(1)}%` : ''}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        {Number.isFinite(averages.component_mad) ? averages.component_mad.toFixed(1) : ''}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          {/* FRID Model Table - Only show for old format */}
          {!isNewModelFormat && (
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2, bgcolor: 'rgba(233, 30, 99, 0.1)', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    FRI-Day (Enhanced)
                  </Typography>
                </Box>
              <TableContainer sx={{ maxHeight: 400, overflow: 'auto', flex: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Forecast</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Accuracy%</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Abs. Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemDetailData.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontSize: '0.7rem' }}>{new Date(row.date).toLocaleDateString()}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }} align="right">{parseFloat(row.forecast_qty_frid || 0).toFixed(0)}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                          {row.APE_frid ? `${((1 - parseFloat(row.APE_frid)) * 100).toFixed(1)}%` : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                          {row.MAD_frid ? parseFloat(row.MAD_frid).toFixed(1) : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Average</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        {averages.frid_forecast ? averages.frid_forecast.toFixed(1) : 'N/A'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        {averages.frid_ape ? `${((1 - averages.frid_ape) * 100).toFixed(1)}%` : 'N/A'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                        {averages.frid_mad ? averages.frid_mad.toFixed(1) : 'N/A'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
          )}

          {/* FRI-Week Model Table - Only show for old format */}
          {!isNewModelFormat && (
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.1)', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    FRI-Week
                  </Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 400, overflow: 'auto', flex: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Forecast</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Accuracy%</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Abs Err</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemDetailData.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontSize: '0.7rem' }}>{new Date(row.date).toLocaleDateString()}</TableCell>
                          <TableCell sx={{ fontSize: '0.7rem' }} align="right">{parseFloat(row.forecast_qty_friw || 0).toFixed(0)}</TableCell>
                          <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                            {formatPercent(row.APE_friw)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                            {formatMad(row.MAD_friw)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Average</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                          {averages.friw_forecast ? averages.friw_forecast.toFixed(1) : 'N/A'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                          {averages.friw_ape ? `${((1 - averages.friw_ape) * 100).toFixed(1)}%` : 'N/A'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                          {averages.friw_mad ? averages.friw_mad.toFixed(1) : 'N/A'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          )}
          </>
          )}
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Model Details Table */}
      <Card elevation={1} sx={{ mb: 4 }}>
        <Box sx={{ p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.08)', borderBottom: '1px solid', borderColor: 'divider', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
            Model Details
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '12px 16px' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem', width: '33.33%' }}>Model Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem', width: '33.33%' }}>Bad Forecast Reason - Past Sales Related</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem', width: '33.33%' }}>Bad Forecast Reason - Current Week Anomalies</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontSize: '0.8rem', verticalAlign: 'top' }}>
                  {(() => {
                    const status = item.status_component_model;
                    return (
                      <Chip
                        label={status || 'N/A'}
                        color={getStatusColor(status)}
                        size="medium"
                        sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                      />
                    );
                  })()}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', verticalAlign: 'top' }}>
                      {(() => {
                    // Normalize itemname function (matches server-side normalization)
                    const normalizeItemname = (str) => {
                      if (!str || str === '') return '';
                      return String(str).trim()
                        .replace(/[^\w\s()]/g, ' ')  // Replace special chars except () with spaces
                        .replace(/\s+/g, ' ')        // Replace multiple spaces with single space
                        .toLowerCase()               // Convert to lowercase
                        .trim();
                    };
                    
                    // Find matching validation summary record for this FRI
                    const itemItemnameNormalized = normalizeItemname(item.itemname || '');
                    const matchingValidation = validationSummaryData.find(vs => {
                      const vsItemnameNormalized = normalizeItemname(vs.itemname || '');
                      return String(vs.foodcourtid || '').trim() === String(item.foodcourtid || '').trim() &&
                             String(vs.restaurant || '').trim() === String(item.restaurant || '').trim() &&
                             vsItemnameNormalized === itemItemnameNormalized;
                    });
                    
                    // Get reasons from validation summary (prefer) or item (fallback)
                    const reasons_pre = matchingValidation?.reasons_pre || item.reasons_pre || '';
                    const status = item.status_component_model;
                    
                    // Format reasons
                    const pastSalesReasons = formatPastSalesReasons(reasons_pre);
                    
                    // If status is Green and no reasons, show "No Performance Issues Detected"
                    if (status === 'Green' && pastSalesReasons.length === 0) {
                      return (
                        <Box sx={{ color: 'success.main', fontSize: '0.8rem', fontStyle: 'italic' }}>
                          No Performance Issues Detected
                          </Box>
                      );
                    }
                    
                    return (
                      <Box sx={{ fontSize: '0.8rem' }}>
                        {pastSalesReasons.length > 0 ? (
                          pastSalesReasons.map((reason, idx) => (
                            <Box key={idx} sx={{ mb: 1, lineHeight: 1.5 }}>
                              {reason}
                            </Box>
                          ))
                        ) : (
                          <Box sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                            None
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', verticalAlign: 'top' }}>
                  {(() => {
                    // Normalize itemname function (matches server-side normalization)
                    const normalizeItemname = (str) => {
                      if (!str || str === '') return '';
                      return String(str).trim()
                        .replace(/[^\w\s()]/g, ' ')  // Replace special chars except () with spaces
                        .replace(/\s+/g, ' ')        // Replace multiple spaces with single space
                        .toLowerCase()               // Convert to lowercase
                        .trim();
                    };
                    
                    // Find matching validation summary record for this FRI
                    const itemItemnameNormalized = normalizeItemname(item.itemname || '');
                    const matchingValidation = validationSummaryData.find(vs => {
                      const vsItemnameNormalized = normalizeItemname(vs.itemname || '');
                      return String(vs.foodcourtid || '').trim() === String(item.foodcourtid || '').trim() &&
                             String(vs.restaurant || '').trim() === String(item.restaurant || '').trim() &&
                             vsItemnameNormalized === itemItemnameNormalized;
                    });
                    
                    // Get reasons from validation summary (prefer) or item (fallback)
                    const reasons_post = matchingValidation?.reasons_post || item.reasons_post || '';
                    const status = item.status_component_model;
                    
                    // Get accuracy from validation summary (mape or weighted_mape)
                    // MAPE is stored as decimal (0-1), accuracy = (1 - MAPE) * 100
                    const mape = matchingValidation?.mape || matchingValidation?.weighted_mape || null;
                    
                    // Format reasons (pass accuracy to filter day-of-week flags if >= 90%)
                    const currentWeekAnomalies = formatCurrentWeekAnomalies(reasons_post, mape);
                    
                    // If status is Green and no reasons, show "No Performance Issues Detected"
                    if (status === 'Green' && currentWeekAnomalies.length === 0) {
                      return (
                        <Box sx={{ color: 'success.main', fontSize: '0.8rem', fontStyle: 'italic' }}>
                          No Performance Issues Detected
                        </Box>
                      );
                    }
                    
                    return (
                      <Box sx={{ fontSize: '0.8rem' }}>
                        {currentWeekAnomalies.length > 0 ? (
                          currentWeekAnomalies.map((anomaly, idx) => (
                            <Box key={idx} sx={{ mb: 1, lineHeight: 1.5 }}>
                              {anomaly}
                            </Box>
                          ))
                        ) : (
                          <Box sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                            None
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Daily Sales Chart - Full Width, then Day of Week Summary on next line */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Daily Sales Chart - Full horizontal span */}
        <Grid item xs={12}>
          <Card elevation={1} sx={{ height: '100%' }}>
            <Box sx={{ p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.08)', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, minHeight: '44px' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
                Daily Sales - Last {(() => {
                  const timePeriodConfig = {
                    '1month': '1 Month',
                    '3months': '3 Months',
                    '6months': '6 Months',
                    '1year': '1 Year',
                    '2years': '2 Years'
                  };
                  return timePeriodConfig[timePeriod] || '6 Months';
                })()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Time Period</InputLabel>
                  <Select
                    value={timePeriod}
                    label="Time Period"
                    onChange={(e) => setTimePeriod(e.target.value)}
                    sx={{ bgcolor: 'white' }}
                  >
                    <MenuItem value="1month">1 Month</MenuItem>
                    <MenuItem value="3months">3 Months</MenuItem>
                    <MenuItem value="6months">6 Months</MenuItem>
                    <MenuItem value="1year">1 Year</MenuItem>
                    <MenuItem value="2years">2 Years</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Days of Week</InputLabel>
                  <Select
                    multiple
                    value={selectedDays}
                    onChange={handleDayFilterChange}
                    label="Days of Week"
                    renderValue={(selected) => (
                      selected.length === DAY_NAME_OPTIONS.length
                        ? 'All Days'
                        : `${selected.length} Selected`
                    )}
                    sx={{ bgcolor: 'white' }}
                  >
                    <MenuItem key="select-all" value={ALL_DAYS_VALUE}>
                      <Checkbox checked={selectedDays.length === DAY_NAME_OPTIONS.length} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {DAY_NAME_OPTIONS.map((day) => (
                      <MenuItem key={day} value={day}>
                        <Checkbox checked={selectedDays.indexOf(day) > -1} />
                        <ListItemText primary={day} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Date Range Slider - Compressed */}
                {dailySalesData.length > 0 && dateRange[0] && dateRange[1] && !Number.isNaN(dateRange[0].getTime()) && !Number.isNaN(dateRange[1].getTime()) && (() => {
                  // Get model date for calculating min date based on time period
                  let modelDateObj = null;
                  if (forecastWindow?.modelDate) {
                    modelDateObj = forecastWindow.modelDate;
                  } else if (selectedModelDate) {
                    try {
                      const year = selectedModelDate.substring(0, 4);
                      const month = selectedModelDate.substring(4, 6);
                      const day = selectedModelDate.substring(6, 8);
                      modelDateObj = new Date(`${year}-${month}-${day}`);
                    } catch (e) {
                      console.error('Error parsing model date:', e);
                    }
                  }
                  
                  if (!modelDateObj) return null;
                  
                  // Get time period configuration
                  const timePeriodConfig = {
                    '1month': { days: 28 },
                    '3months': { days: 91 },
                    '6months': { days: 182 },
                    '1year': { days: 364 },
                    '2years': { days: 730 }
                  };
                  const selectedConfig = timePeriodConfig[timePeriod] || timePeriodConfig['6months'];
                  
                  // Calculate min date based on time period (model_date - period days)
                  const minDate = new Date(modelDateObj);
                  minDate.setDate(minDate.getDate() - selectedConfig.days);
                  
                  // Max date is model_date + 7 days (for forecast)
                  const maxDate = new Date(modelDateObj);
                  maxDate.setDate(maxDate.getDate() + 7);
                  
                  const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24));
                  
                  // Convert date range to slider values (0-100)
                  const startPercent = Math.max(0, Math.min(100, ((dateRange[0] - minDate) / (maxDate - minDate)) * 100));
                  const endPercent = Math.max(0, Math.min(100, ((dateRange[1] - minDate) / (maxDate - minDate)) * 100));
                  
                  const handleSliderChange = (event, newValue) => {
                    const [startPercent, endPercent] = newValue;
                    const startDate = new Date(minDate);
                    startDate.setDate(startDate.getDate() + Math.round((startPercent / 100) * totalDays));
                    const endDate = new Date(minDate);
                    endDate.setDate(endDate.getDate() + Math.round((endPercent / 100) * totalDays));
                    setDateRange([startDate, endDate]);
                  };
                  
                  return (
                    <FormControl size="small" sx={{ minWidth: 200, position: 'relative' }}>
                      <InputLabel 
                        shrink={true}
                        sx={{ 
                          bgcolor: 'rgba(76, 175, 80, 0.08)',
                          px: 0.5,
                          transform: 'translate(14px, -9px) scale(0.75)',
                          transformOrigin: 'top left',
                          zIndex: 1
                        }}
                      >
                        Date Range Filter
                      </InputLabel>
                      <Box sx={{ 
                        width: 200, 
                        height: '40px',
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        bgcolor: 'transparent',
                        borderRadius: 1,
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        justifyContent: 'center',
                        px: 1,
                        position: 'relative',
                        '&:hover': {
                          borderColor: 'rgba(0, 0, 0, 0.87)'
                        },
                        '&:focus-within': {
                          borderColor: 'primary.main',
                          borderWidth: '2px'
                        }
                      }}>
                        <Slider
                          value={[startPercent, endPercent]}
                          onChange={handleSliderChange}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) => {
                            const date = new Date(minDate);
                            date.setDate(date.getDate() + Math.round((value / 100) * totalDays));
                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                          }}
                          min={0}
                          max={100}
                          size="small"
                          sx={{ width: '90%' }}
                        />
                      </Box>
                    </FormControl>
                  );
                })()}
                <FormControlLabel
                  control={
                    <Switch
                      checked={showNonZeroOnly}
                      onChange={(e) => setShowNonZeroOnly(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Non-Zero Only"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                />
              </Box>
            </Box>
            <Box sx={{ p: 3 }}>
              {loadingSales ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                  <CircularProgress />
                </Box>
              ) : salesError ? (
                <Alert severity="error">Error loading sales data: {salesError}</Alert>
              ) : dailySalesData.length === 0 ? (
                <Alert severity="info">No sales data available for this item</Alert>
              ) : (() => {
                // Get model date for filtering
                let modelDateObj = null;
                if (forecastWindow?.modelDate) {
                  modelDateObj = forecastWindow.modelDate;
                } else if (selectedModelDate) {
                  try {
                    const year = selectedModelDate.substring(0, 4);
                    const month = selectedModelDate.substring(4, 6);
                    const day = selectedModelDate.substring(6, 8);
                    modelDateObj = new Date(`${year}-${month}-${day}`);
                  } catch (e) {
                    console.error('Error parsing model date:', e);
                  }
                }
                
                // Get time period configuration
                const timePeriodConfig = {
                  '1month': { days: 28, label: '1 Month' },
                  '3months': { days: 91, label: '3 Months' },
                  '6months': { days: 182, label: '6 Months' },
                  '1year': { days: 364, label: '1 Year' },
                  '2years': { days: 730, label: '2 Years' }
                };
                const selectedConfig = timePeriodConfig[timePeriod] || timePeriodConfig['6months'];
                
                // Filter data based on selected time period relative to model date
                let periodData = dailySalesData;
                if (modelDateObj && !Number.isNaN(modelDateObj.getTime())) {
                  // Filter to dates within the selected period before model date
                  const periodStart = new Date(modelDateObj);
                  periodStart.setDate(periodStart.getDate() - selectedConfig.days);
                  
                  // For all periods, include 1 week after model date to show forecast
                  const periodEnd = new Date(modelDateObj);
                  periodEnd.setDate(periodEnd.getDate() + 7); // Add 1 week (7 days) for all periods
                  
                  periodData = dailySalesData.filter(item => {
                    const itemDate = new Date(item.date);
                    if (Number.isNaN(itemDate.getTime())) return false;
                    // Include dates from (modelDate - periodDays) to (modelDate + 1 week)
                    return itemDate >= periodStart && itemDate <= periodEnd;
                  });
                } else {
                  // Fallback: if no model date, use last N days
                  periodData = dailySalesData.slice(-selectedConfig.days);
                }
                
                // Apply date range slider filter if set
                let dateFilteredData = periodData;
                if (dateRange[0] && dateRange[1] && !Number.isNaN(dateRange[0].getTime()) && !Number.isNaN(dateRange[1].getTime())) {
                  dateFilteredData = periodData.filter(item => {
                    const itemDate = new Date(item.date);
                    if (Number.isNaN(itemDate.getTime())) return false;
                    return itemDate >= dateRange[0] && itemDate <= dateRange[1];
                  });
                }
                
                // Filter data based on toggle state
                let filteredData = dateFilteredData;
                if (showNonZeroOnly) {
                  filteredData = dateFilteredData.filter(item => {
                    const hasSales = parseFloat(item.quantity || 0) > 0;
                    const hasForecast = (() => {
                      if (!item) return false;
                      const fcVal = item.forecast_component ?? item.forecast_qty_component_model ?? item.forecast_qty;
                      const exists = fcVal !== null && fcVal !== undefined;
                      if (!exists) return false;
                      if (!forecastWindow) return true;
                      const d = new Date(item.date);
                      if (Number.isNaN(d.getTime())) return false;
                      return d >= forecastWindow.start && d <= forecastWindow.end;
                    })();
                    // Remove zeros in both pre and post model-date periods
                    return hasSales || hasForecast;
                  });
                }
                
                const activeDaySet = new Set(selectedDays.length ? selectedDays : DAY_NAME_OPTIONS);
                filteredData = filteredData.filter(item => {
                  const d = new Date(item.date);
                  if (Number.isNaN(d.getTime())) return false;
                  const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
                  return activeDaySet.has(dayName);
                });
                
                // Calculate mean/average quantity (excluding 0 days - only days with sales)
                const soldDays = periodData.filter(item => parseFloat(item.quantity || 0) > 0);
                const totalQuantity = soldDays.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
                const meanQuantity = soldDays.length > 0 ? totalQuantity / soldDays.length : 0;
                
                // Add mean data to chart data (rounded to 1 decimal place)
                const mgByDate = new Map(
                  (mgData || []).map((r) => [String(r.date || '').slice(0, 10), parseFloat(r.quantity)])
                );
                const chartData = filteredData.map((item) => {
                  const itemDate = new Date(item.date);
                  const quantity = parseFloat(item.quantity || 0);
                  const { lo, hi } = resolveChartBandFields(item, itemDate, dayBandByDate);
                  const dateKey = String(item.date || '').slice(0, 10);
                  const mgRaw = mgByDate.get(dateKey);
                  const mg_quantity = Number.isFinite(mgRaw) ? mgRaw : 0;
                  
                  // Determine if date is post-model-date
                  let isPostModelDate = false;
                  if (modelDateObj && !Number.isNaN(modelDateObj.getTime()) && !Number.isNaN(itemDate.getTime())) {
                    isPostModelDate = itemDate > modelDateObj;
                  }

                  const inForecastWindow = (() => {
                    if (!forecastWindow || Number.isNaN(itemDate.getTime())) return false;
                    return itemDate >= forecastWindow.start && itemDate <= forecastWindow.end;
                  })();
                  const forecastRaw = item.forecast_component ?? item.forecast_qty_component_model ?? item.forecast_qty;
                  const forecastNum = forecastRaw != null ? parseFloat(forecastRaw) : null;
                  const hasForecastDay = inForecastWindow && Number.isFinite(forecastNum) && forecastNum > 0;
                  
                  // Create chart data object, excluding forecast_pre to avoid showing it in legend
                  return {
                    date: item.date,
                    quantity: quantity,
                    // Add quantity_post for lighter thickness line in post-model-date period
                    quantity_post: isPostModelDate ? quantity : null,
                    mean: parseFloat(meanQuantity.toFixed(1)),
                    description: item.description || '',
                    description_combo1: item.description_combo1 || '',
                    description_combo2: item.description_combo2 || '',
                    mg_quantity,
                    forecast_plot: (() => {
                      if (!forecastWindow) {
                        return Number.isFinite(forecastNum) && forecastNum > 0 ? forecastNum : 0;
                      }
                      if (Number.isNaN(itemDate.getTime())) return null;
                      if (!inForecastWindow) return null;
                      return Number.isFinite(forecastNum) && forecastNum > 0 ? forecastNum : 0;
                    })(),
                    forecast_lower_plot: hasForecastDay && lo != null ? parseFloat(lo) : null,
                    forecast_upper_plot: hasForecastDay && hi != null ? parseFloat(hi) : null,
                  };
                });

                const forecastDataPoints = chartData.filter(
                  (item) => item.forecast_plot !== null && item.forecast_plot !== undefined
                );
                // Only highlight the last forecast point with a solid dot
                const lastForecastDate =
                  forecastDataPoints.length > 0
                    ? forecastDataPoints[forecastDataPoints.length - 1].date
                    : null;
                const showForecastDots = !!lastForecastDate;
                
                // Date formatter function (date + day of week)
                const formatDate = (value) => {
                  const date = new Date(value);
                  if (Number.isNaN(date.getTime())) return value;
                  const day = date.getDate().toString().padStart(2, '0');
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  const month = monthNames[date.getMonth()];
                  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
                  return `${day}-${month} (${dayOfWeek})`;
                };
                
                // Get model_date for reference line - need to match the date format in chartData
                let modelDateForLine = null;
                if (forecastWindow?.modelDate) {
                  // Use modelDate from forecastWindow if available
                  const modelDate = forecastWindow.modelDate;
                  if (!Number.isNaN(modelDate.getTime())) {
                    modelDateForLine = modelDate.toISOString().split('T')[0];
                  }
                } else if (selectedModelDate) {
                  // Fallback: parse selectedModelDate (YYYYMMDD format)
                  try {
                    const year = selectedModelDate.substring(0, 4);
                    const month = selectedModelDate.substring(4, 6);
                    const day = selectedModelDate.substring(6, 8);
                    const modelDate = new Date(`${year}-${month}-${day}`);
                    if (!Number.isNaN(modelDate.getTime())) {
                      modelDateForLine = modelDate.toISOString().split('T')[0];
                    }
                  } catch (e) {
                    console.error('Error parsing model date:', e);
                  }
                }
                
                // When "Non-Zero Days Only" is enabled, show a line chart using
                // the already filtered non-zero data and connect gaps.
                if (showNonZeroOnly) {
                  return (
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tickFormatter={formatDate}
                        />
                        <YAxis 
                          label={{ value: 'Sales Quantity', angle: -90, position: 'insideLeft' }}
                          tick={{ fontSize: 12 }}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend />
                        {modelDateForLine && (
                          <ReferenceLine 
                            x={modelDateForLine} 
                            stroke="#666" 
                            strokeDasharray="5 5" 
                            strokeWidth={2}
                            label={{ value: "Model Date", position: "top", fill: "#666", fontSize: 12 }}
                          />
                        )}
                        <Area
                          type="monotone"
                          dataKey={forecastBandRangeDataKey}
                          stroke="none"
                          fill={FORECAST_BAND_CHART.bandFill}
                          connectNulls={false}
                          isAnimationActive={false}
                          name="Forecast Range"
                          legendType="square"
                          tooltipType="none"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="quantity" 
                          stroke="#1976d2" 
                          strokeWidth={2}
                          dot={false}
                          connectNulls={true}
                          name="Sales Quantity"
                          legendType="square"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="quantity_post" 
                          stroke="#1976d2" 
                          strokeWidth={1.5}
                          dot={false}
                          connectNulls={true}
                          name=""
                          legendType="none"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="mean" 
                          stroke="#4caf50" 
                          strokeWidth={1.5}
                          strokeDasharray="3 3"
                          dot={false}
                          name={`Mean (${meanQuantity.toFixed(1)})`}
                          legendType="square"
                        />
                        <Line
                          type="monotone"
                          dataKey="forecast_plot"
                          stroke={FORECAST_BAND_CHART.forecast}
                          strokeWidth={2}
                          strokeDasharray="8 4"
                          dot={
                            showForecastDots
                              ? (props) =>
                                  props?.payload?.date === lastForecastDate ? (
                                    <circle
                                      cx={props.cx}
                                      cy={props.cy}
                                      r={4}
                                      fill="#ef5350"
                                      stroke="#ef5350"
                                    />
                                  ) : null
                              : false
                          }
                          connectNulls={true}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          name="Forecast"
                          legendType="square"
                        />
                        {mgTypeForItem(item.itemname) && (
                          <Line
                            type="monotone"
                            dataKey="mg_quantity"
                            stroke="#8e24aa"
                            strokeWidth={2}
                            strokeDasharray="8 4"
                            dot={false}
                            connectNulls={true}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            name="MG"
                            legendType="line"
                          />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  );
                } else {
                  return (
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tickFormatter={formatDate}
                        />
                        <YAxis 
                          label={{ value: 'Sales Quantity', angle: -90, position: 'insideLeft' }}
                          tick={{ fontSize: 12 }}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend />
                        {modelDateForLine && (
                          <ReferenceLine 
                            x={modelDateForLine} 
                            stroke="#666" 
                            strokeDasharray="5 5" 
                            strokeWidth={2}
                            label={{ value: "Model Date", position: "top", fill: "#666", fontSize: 12 }}
                          />
                        )}
                        <Area
                          type="monotone"
                          dataKey={forecastBandRangeDataKey}
                          stroke="none"
                          fill={FORECAST_BAND_CHART.bandFill}
                          connectNulls={false}
                          isAnimationActive={false}
                          name="Forecast Range"
                          legendType="square"
                          tooltipType="none"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="quantity" 
                          stroke="#1976d2" 
                          strokeWidth={2}
                          dot={false}
                          connectNulls={true}
                          name="Sales Quantity"
                          legendType="square"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="quantity_post" 
                          stroke="#1976d2" 
                          strokeWidth={1.5}
                          dot={false}
                          connectNulls={true}
                          name=""
                          legendType="none"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="mean" 
                          stroke="#4caf50" 
                          strokeWidth={1.5}
                          strokeDasharray="3 3"
                          dot={false}
                          name={`Mean (${meanQuantity.toFixed(1)})`}
                          legendType="square"
                        />
                        <Line
                          type="monotone"
                          dataKey="forecast_plot"
                          stroke={FORECAST_BAND_CHART.forecast}
                          strokeWidth={2}
                          strokeDasharray="8 4"
                          dot={
                            showForecastDots
                              ? (props) =>
                                  props?.payload?.date === lastForecastDate ? (
                                    <circle
                                      cx={props.cx}
                                      cy={props.cy}
                                      r={4}
                                      fill="#ef5350"
                                      stroke="#ef5350"
                                    />
                                  ) : null
                              : false
                          }
                          connectNulls={true}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          name="Forecast"
                          legendType="square"
                        />
                        {mgTypeForItem(item.itemname) && (
                          <Line
                            type="monotone"
                            dataKey="mg_quantity"
                            stroke="#8e24aa"
                            strokeWidth={2}
                            strokeDasharray="8 4"
                            dot={false}
                            connectNulls={true}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            name="MG"
                            legendType="line"
                          />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  );
                }
              })()}
            </Box>
          </Card>
        </Grid>

        {/* Session Slot-Wise Distribution - only when not in Predict Mode */}
        {!debugMode && salePeriodTrendData.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card elevation={1} sx={{ height: '100%' }}>
              <Box sx={{ p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.08)', borderBottom: '1px solid', borderColor: 'divider', minHeight: '44px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
                  Session Slot-Wise Distribution
                </Typography>
              </Box>
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 0.5 } }}>
                <Box sx={{ height: 420 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={salePeriodTrendData}
                      margin={{ top: 2, right: 16, left: 16, bottom: 40 }}
                      barSize={24}
                      barCategoryGap="8%"
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        type="category"
                        dataKey="date"
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          if (Number.isNaN(date.getTime())) return value;
                          const day = date.getDate().toString().padStart(2, '0');
                          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                          const month = monthNames[date.getMonth()];
                          const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
                          return `${day}-${month} (${dayOfWeek})`;
                        }}
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis
                        type="number"
                        domain={salePeriodYDomain}
                        allowDataOverflow
                        tickFormatter={(v) => {
                          const h = Math.floor(v / 60);
                          const m = v % 60;
                          return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                        }}
                        tick={{ fontSize: 11 }}
                        label={{ value: 'Time', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                      />
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (!active || !payload || payload.length === 0) return null;
                          const p = payload[0]?.payload;
                          if (!p) return null;
                          return (
                            <div style={{
                              backgroundColor: 'rgba(255,255,255,0.95)',
                              border: '1px solid #ccc',
                              borderRadius: 4,
                              padding: 8,
                              fontSize: 12
                            }}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                {p.date ? new Date(p.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                              </div>
                              <div>First: {p.first_order_time || '—'}</div>
                              <div>Last: {p.last_order_time || '—'}</div>
                              <div>Revenue Coverage: {p.revenue_coverage != null ? `${p.revenue_coverage.toFixed(1)}%` : '—'}</div>
                            </div>
                          );
                        }}
                      />
                      <Bar dataKey="offset" stackId="sp" fill="transparent" />
                      <Bar dataKey="span" stackId="sp" name="Sale period" radius={[4, 4, 0, 0]}>
                        {salePeriodTrendData.map((entry, index) => (
                          <Cell key={`${entry.date}-${index}`} fill={entry.displayColor ?? entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.2, justifyContent: 'center' }}>
                  {[
                    { label: 'Normal', color: '#66bb6a' },
                    { label: 'Late Start', color: '#fb8c00' },
                    { label: 'Early End', color: '#f48fb1' },
                    { label: 'Late Start + Early End', color: '#e53935' }
                  ].map((entry) => (
                    <Box key={entry.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: entry.color, border: '1px solid rgba(0,0,0,0.2)' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                        {entry.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Component Insights Table - middle; hidden for Combo 1+2 aggregate */}
        {!isAggregateItem && (
        <Grid item xs={12} md={!debugMode && salePeriodTrendData.length > 0 ? 5 : 9}>
          <Card elevation={1} sx={{ height: '100%' }}>
            <Box sx={{ p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.08)', borderBottom: '1px solid', borderColor: 'divider', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
                Component Insights
              </Typography>
            </Box>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              {loadingFriComponentStats && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {!loadingFriComponentStats && friComponentStatsError && (
                <Alert severity="error" sx={{ fontSize: '0.75rem' }}>
                  {friComponentStatsError}
                </Alert>
              )}
              {!loadingFriComponentStats && !friComponentStatsError && (
                <>
                  <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel id="component-category-select-label" sx={{ fontSize: '0.75rem' }}>
                        Component Category
                      </InputLabel>
                      <Select
                        labelId="component-category-select-label"
                        value={selectedComponentCategory}
                        label="Component Category"
                        onChange={(e) => setSelectedComponentCategory(e.target.value)}
                        sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.75 } }}
                      >
                        {componentInsights.categoryOptions.map((category) => (
                          <MenuItem key={category} value={category} sx={{ fontSize: '0.75rem' }}>
                            {category}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '4px 8px', fontSize: '0.7rem' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Component</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Lift Factor</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Tag</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedComponentRows.length > 0 ? (
                        paginatedComponentRows.map((r) => (
                          <TableRow
                            key={`${r.component_name}-${r.component_category}`}
                            onClick={() => setSelectedComponentDetail(r)}
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            <TableCell>{toProperCase(r.component_name)}</TableCell>
                            <TableCell align="right">
                              {Number.isFinite(Number(r.avg_sale_ratio_1_over_0))
                                ? `${((Number(r.avg_sale_ratio_1_over_0) - 1) * 100).toFixed(0)}%`
                                : '—'}
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={r.impact_tag}
                                color={getImpactChipColor(r.impact_tag)}
                                size="small"
                                sx={{ fontSize: '0.65rem', height: 22 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} sx={{ color: 'text.secondary' }}>
                            No components found for selected category
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      Page {componentPage + 1} of {componentPageCount}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={componentPage === 0}
                        onClick={() => setComponentPage((prev) => Math.max(0, prev - 1))}
                        sx={{ minWidth: 72, fontSize: '0.7rem', py: 0.25 }}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={componentPage >= componentPageCount - 1}
                        onClick={() => setComponentPage((prev) => Math.min(componentPageCount - 1, prev + 1))}
                        sx={{ minWidth: 56, fontSize: '0.7rem', py: 0.25 }}
                      >
                        Next
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
              {!loadingFriComponentStats && !friComponentStatsError && (
                <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 1.5, fontSize: '0.7rem' }}>
                  Click on Individual Components for Deep Dive
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Component detail dialog: chart + table (Days Sold, Days Not Sold, Lift Factor) */}
        <Dialog
          open={Boolean(selectedComponentDetail)}
          onClose={() => setSelectedComponentDetail(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          {selectedComponentDetail && (
            <>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
                <Typography variant="h6">{toProperCase(selectedComponentDetail.component_name)}</Typography>
                <IconButton size="small" onClick={() => setSelectedComponentDetail(null)} aria-label="close">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ pt: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Median units when component present vs absent</Typography>
                <Box sx={{ height: 220, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: 'When present', value: Number(selectedComponentDetail.avg_sale_flag_1) || 0, fill: (selectedComponentDetail.avg_sale_ratio_1_over_0 != null && selectedComponentDetail.avg_sale_ratio_1_over_0 < 1) ? '#e57373' : '#4caf50' },
                        { name: 'When absent', value: Number(selectedComponentDetail.avg_sale_flag_0) || 0, fill: '#9e9e9e' }
                      ]}
                      margin={{ top: 24, right: 16, left: 8, bottom: 24 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Bar dataKey="value" name="Median units" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 11, formatter: (value) => (Number.isFinite(value) ? Math.round(value) : '') }} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '8px 12px' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Days Sold</TableCell>
                      <TableCell align="right">{selectedComponentDetail.days_with_flag_1 ?? '—'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Days Not Sold</TableCell>
                      <TableCell align="right">{selectedComponentDetail.days_with_flag_0 ?? '—'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Lift Factor</TableCell>
                      <TableCell align="right">
                        {(selectedComponentDetail.avg_sale_ratio_1_over_0 != null && Number.isFinite(selectedComponentDetail.avg_sale_ratio_1_over_0))
                          ? `${((Number(selectedComponentDetail.avg_sale_ratio_1_over_0) - 1) * 100).toFixed(0)}%`
                          : '—'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </DialogContent>
            </>
          )}
        </Dialog>

        {/* Day of Week Summary Table - right side, same row */}
        <Grid item xs={12} md={isAggregateItem ? (!debugMode && salePeriodTrendData.length > 0 ? 8 : 12) : 3}>
          {dailySalesData.length > 0 ? (
            <Card elevation={1} sx={{ height: '100%' }}>
              <Box sx={{ p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.08)', borderBottom: '1px solid', borderColor: 'divider', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
                  Day of Week Summary
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '4px 8px' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Day of Week</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Days Sold</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Median Units</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Get model date for filtering
                      let modelDateObj = null;
                      if (forecastWindow?.modelDate) {
                        modelDateObj = forecastWindow.modelDate;
                      } else if (selectedModelDate) {
                        try {
                          const year = selectedModelDate.substring(0, 4);
                          const month = selectedModelDate.substring(4, 6);
                          const day = selectedModelDate.substring(6, 8);
                          modelDateObj = new Date(`${year}-${month}-${day}`);
                        } catch (e) {
                          console.error('Error parsing model date:', e);
                        }
                      }
                      
                      // Get time period configuration
                      const timePeriodConfig = {
                        '1month': { days: 28 },
                        '3months': { days: 91 },
                        '6months': { days: 182 },
                        '1year': { days: 364 },
                        '2years': { days: 730 }
                      };
                      const selectedConfig = timePeriodConfig[timePeriod] || timePeriodConfig['6months'];
                      
                      // Filter data based on selected time period relative to model date
                      let periodData = dailySalesData;
                      if (modelDateObj && !Number.isNaN(modelDateObj.getTime())) {
                        // Filter to dates within the selected period before model date
                        const periodStart = new Date(modelDateObj);
                        periodStart.setDate(periodStart.getDate() - selectedConfig.days);
                        
                        // For all periods, include 1 week after model date to show forecast
                        const periodEnd = new Date(modelDateObj);
                        periodEnd.setDate(periodEnd.getDate() + 7); // Add 1 week (7 days) for all periods
                        
                        periodData = dailySalesData.filter(item => {
                          const itemDate = new Date(item.date);
                          if (Number.isNaN(itemDate.getTime())) return false;
                          // Include dates from (modelDate - periodDays) to (modelDate + 1 week)
                          return itemDate >= periodStart && itemDate <= periodEnd;
                        });
                      } else {
                        // Fallback: if no model date, use last N days
                        periodData = dailySalesData.slice(-selectedConfig.days);
                      }
                      
                      // Group by day of week
                      const dayGroups = {
                        0: { name: 'Sunday', quantities: [] },
                        1: { name: 'Monday', quantities: [] },
                        2: { name: 'Tuesday', quantities: [] },
                        3: { name: 'Wednesday', quantities: [] },
                        4: { name: 'Thursday', quantities: [] },
                        5: { name: 'Friday', quantities: [] },
                        6: { name: 'Saturday', quantities: [] }
                      };
                      
                      periodData.forEach(item => {
                        const date = new Date(item.date);
                        const dayOfWeek = date.getDay();
                        const quantity = parseFloat(item.quantity) || 0;
                        dayGroups[dayOfWeek].quantities.push(quantity);
                      });
                      
                      // Order: Monday to Sunday
                      const dayOrder = [1, 2, 3, 4, 5, 6, 0];
                      const dayRows = dayOrder.map(dayKey => {
                        const day = dayGroups[dayKey];
                        const soldDays = day.quantities.filter(q => q > 0).length;
                        const soldQuantities = day.quantities.filter(q => q > 0);
                        const totalQuantity = soldQuantities.reduce((sum, q) => sum + q, 0);
                        const avgQuantity = soldDays > 0 ? totalQuantity / soldDays : 0;
                        
                        return {
                          dayKey,
                          day,
                          soldDays,
                          avgQuantity
                        };
                      });
                      
                      // Calculate summary: All Days - Average Daily Units Sold
                      const allSoldQuantities = periodData
                        .map(item => parseFloat(item.quantity || 0))
                        .filter(q => q > 0);
                      const allDaysSold = allSoldQuantities.length;
                      const allDaysAvg = allSoldQuantities.length > 0
                        ? allSoldQuantities.reduce((sum, q) => sum + q, 0) / allSoldQuantities.length
                        : 0;
                      
                      return (
                        <>
                          {dayRows.map(({ dayKey, day, soldDays, avgQuantity }) => (
                            <TableRow key={dayKey}>
                              <TableCell sx={{ fontWeight: 500, fontSize: '0.7rem' }}>{day.name}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>{soldDays}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                                {avgQuantity.toFixed(1)}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.04)' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>All Days</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>{allDaysSold}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                              {allDaysAvg.toFixed(1)}
                            </TableCell>
                          </TableRow>
                        </>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          ) : (
            <Card elevation={1} sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
              <Alert severity="info">No sales data available</Alert>
            </Card>
          )}
        </Grid>
      </Grid>
        </>
      )}

      {/* Session Level (no charts / no extra bottom tables) */}
      {currentTab === 1 && (
        <>
          {/* Summary Cards */}
          {loadingAggregatedSession ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px" sx={{ mb: 3 }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, mb: 4, width: '100%', justifyContent: 'flex-start' }}>
                <Card
                  elevation={1}
                  sx={{
                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                    height: 120,
                    minHeight: 120,
                    display: 'flex',
                    flex: 1,
                    minWidth: 0,
                    maxWidth: 280
                  }}
                >
                <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      #Sessions Sold
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {sessionStats.sessionsSold}
                    </Typography>
                  </CardContent>
                </Card>
            </Box>
          )}

          {/* Filter as pills */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Select Session
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {uniqueSessions.map((s) => {
                const hasData = s.hasData !== false; // Default to true if not set
                const isGreyed = !hasData;
                
                return (
                <Chip
                  key={s.id}
                  label={s.label}
                  onClick={() => {
                    if (!isGreyed) {
                      setSelectedSessionId(s.id);
                    }
                  }}
                  color={selectedSessionId === s.id ? 'primary' : 'default'}
                  variant={selectedSessionId === s.id ? 'filled' : 'outlined'}
                  disabled={isGreyed}
                  sx={{
                    cursor: isGreyed ? 'not-allowed' : 'pointer',
                    fontWeight: selectedSessionId === s.id ? 600 : 400,
                    opacity: isGreyed ? 0.5 : 1,
                    '&:hover': {
                      backgroundColor: isGreyed 
                        ? 'transparent'
                        : selectedSessionId === s.id
                          ? 'primary.main'
                          : 'action.hover'
                    }
                  }}
                />
                );
              })}
            </Box>
          </Box>

          {loadingSession ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="250px">
              <CircularProgress />
            </Box>
          ) : sessionError ? (
            <Alert severity="error">Error loading session data: {sessionError}</Alert>
          ) : filteredSessionRows.length === 0 ? (
            <Alert severity="info">No session-level records found for this item.</Alert>
          ) : (
            <>
              {/* Actual, Existing, and New Model Tables - Same row for new format */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Actual Data Table */}
                <Grid item xs={12} md={isNewModelFormat ? (!debugMode ? 6 : 4) : 12}>
                  <Box sx={{ display: 'flex', justifyContent: isNewModelFormat ? 'flex-start' : 'center' }}>
                    <Card elevation={1} sx={{ maxWidth: isNewModelFormat ? '100%' : 500, width: '100%' }}>
                      <Box sx={{ p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.08)', borderBottom: '1px solid', borderColor: 'divider', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
                          Actual Data
                        </Typography>
                      </Box>
                      <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Actual Quantity Sold</TableCell>
                              {isNewModelFormat && (
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="center">In Range</TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredSessionRows.map((r, idx) => {
                              const inRangeLabel = getSessionInRangeLabel(r, dayBandByDate);
                              return (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontSize: '0.7rem' }}>{r.date ? new Date(r.date).toLocaleDateString() : ''}</TableCell>
                                <TableCell sx={{ fontSize: '0.7rem' }} align="right">{parseFloat(r.actual_qty || 0).toFixed(0)}</TableCell>
                                {isNewModelFormat && (
                                  <TableCell sx={{ fontSize: '0.7rem' }} align="center">
                                    <Chip
                                      label={inRangeLabel}
                                      size="small"
                                      color={getInRangeChipColor(inRangeLabel)}
                                      variant={inRangeLabel === '—' ? 'outlined' : 'filled'}
                                      sx={{ fontSize: '0.65rem', height: 20, minWidth: 44 }}
                                    />
                                  </TableCell>
                                )}
                              </TableRow>
                              );
                            })}
                            <TableRow sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Average</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {sessionAverages.actual !== null ? sessionAverages.actual.toFixed(1) : 'N/A'}
                              </TableCell>
                              {isNewModelFormat && (
                                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                  {sessionInRangePct != null && Number.isFinite(sessionInRangePct)
                                    ? `${(sessionInRangePct * 100).toFixed(0)}%`
                                    : '—'}
                                </TableCell>
                              )}
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  </Box>
                </Grid>

                {/* Component Model - Label changes based on format */}
                <Grid item xs={12} sm={6} md={isNewModelFormat ? 6 : 3}>
                  <Card elevation={1}>
                    <Box sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.1)', borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {isNewModelFormat ? (debugMode ? 'New Model (Combined)' : 'Forecast Model') : 'FRI-Day (Component)'}
                      </Typography>
                    </Box>
                    <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Forecast</TableCell>
                            {isNewModelFormat && (
                              <>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Range</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="center">In Range</TableCell>
                              </>
                            )}
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Accuracy%</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Abs Err</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredSessionRows.map((r, idx) => {
                            const { lower, upper } = getSessionBandFields(r, dayBandByDate);
                            const inRangeLabel = getSessionInRangeLabel(r, dayBandByDate);
                            return (
                            <TableRow key={idx}>
                              <TableCell sx={{ fontSize: '0.7rem' }}>{r.date ? new Date(r.date).toLocaleDateString() : ''}</TableCell>
                              <TableCell sx={{ fontSize: '0.7rem' }} align="right">{parseFloat(r.forecast_qty_component_model || r.forecast_qty || 0).toFixed(0)}</TableCell>
                              {isNewModelFormat && (
                                <>
                                  <TableCell sx={{ fontSize: '0.7rem' }} align="right">{formatForecastRange(lower, upper)}</TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }} align="center">
                                    <Chip
                                      label={inRangeLabel}
                                      size="small"
                                      color={getInRangeChipColor(inRangeLabel)}
                                      variant={inRangeLabel === '—' ? 'outlined' : 'filled'}
                                      sx={{ fontSize: '0.65rem', height: 20, minWidth: 44 }}
                                    />
                                  </TableCell>
                                </>
                              )}
                              <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                                {formatPercent(r.APE_component_model)}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                                {formatMad(r.MAD_component_model)}
                              </TableCell>
                            </TableRow>
                            );
                          })}
                          <TableRow sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Average</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                              {sessionAverages.forecast_component !== null ? sessionAverages.forecast_component.toFixed(1) : 'N/A'}
                            </TableCell>
                            {isNewModelFormat && (
                              <>
                                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>—</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                  {sessionInRangePct != null && Number.isFinite(sessionInRangePct)
                                    ? `${(sessionInRangePct * 100).toFixed(0)}%`
                                    : '—'}
                                </TableCell>
                              </>
                            )}
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                              {sessionAverages.ape_component !== null ? `${((1 - sessionAverages.ape_component) * 100).toFixed(1)}%` : ''}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                              {sessionAverages.mad_component !== null ? sessionAverages.mad_component.toFixed(1) : ''}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                </Grid>

                {/* FRI-Day (Enhanced) - Only show for old format */}
                {!isNewModelFormat && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={1}>
                      <Box sx={{ p: 2, bgcolor: 'rgba(233, 30, 99, 0.1)', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          FRI-Day (Enhanced)
                        </Typography>
                      </Box>
                      <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Forecast</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Accuracy%</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Abs Err</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredSessionRows.map((r, idx) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontSize: '0.7rem' }}>{r.date ? new Date(r.date).toLocaleDateString() : ''}</TableCell>
                                <TableCell sx={{ fontSize: '0.7rem' }} align="right">{parseFloat(r.forecast_qty_frid || 0).toFixed(0)}</TableCell>
                                <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                                  {formatPercent(r.APE_frid)}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                                  {formatMad(r.MAD_frid)}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Average</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {sessionAverages.forecast_frid !== null ? sessionAverages.forecast_frid.toFixed(1) : 'N/A'}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {sessionAverages.ape_frid !== null ? `${((1 - sessionAverages.ape_frid) * 100).toFixed(1)}%` : ''}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {sessionAverages.mad_frid !== null ? sessionAverages.mad_frid.toFixed(1) : ''}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  </Grid>
                )}

                {/* FRI-Week - Only show for old format */}
                {!isNewModelFormat && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Card elevation={1}>
                      <Box sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.1)', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          FRI-Week
                        </Typography>
                      </Box>
                      <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Forecast</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Accuracy%</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }} align="right">Abs Err</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredSessionRows.map((r, idx) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontSize: '0.7rem' }}>{r.date ? new Date(r.date).toLocaleDateString() : ''}</TableCell>
                                <TableCell sx={{ fontSize: '0.7rem' }} align="right">{parseFloat(r.forecast_qty_friw || 0).toFixed(0)}</TableCell>
                                <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                                  {formatPercent(r.APE_friw)}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.7rem' }} align="right">
                                  {formatMad(r.MAD_friw)}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: 'grey.50', fontWeight: 600 }}>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Average</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {sessionAverages.forecast_friw !== null ? sessionAverages.forecast_friw.toFixed(1) : 'N/A'}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {sessionAverages.ape_friw !== null ? `${((1 - sessionAverages.ape_friw) * 100).toFixed(1)}%` : ''}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {sessionAverages.mad_friw !== null ? sessionAverages.mad_friw.toFixed(1) : ''}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  </Grid>
                )}
              </Grid>

              {/* Model Details Table */}
              <Card elevation={1} sx={{ mt: 4 }}>
                <Box sx={{ p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.08)', borderBottom: '1px solid', borderColor: 'divider', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
                    Model Details
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '12px 16px' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem', width: '33.33%' }}>Model Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem', width: '33.33%' }}>Bad Forecast Reason - Past Sales Related</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem', width: '33.33%' }}>Bad Forecast Reason - Current Week Anomalies</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.8rem', verticalAlign: 'top' }}>
                          {(() => {
                            const worstStatus = (() => {
                              const statuses = [
                                sessionModelStatuses.componentStatus,
                                ...(isNewModelFormat ? [] : [sessionModelStatuses.fridStatus, sessionModelStatuses.friwStatus, sessionModelStatuses.bestStatus])
                              ].filter(s => s);
                              return statuses.find(s => s === 'Red') || 
                                     statuses.find(s => s === 'Orange') || 
                                     statuses.find(s => s === 'Green');
                            })();
                            return (
                            <Chip
                                label={worstStatus || 'N/A'}
                                color={worstStatus ? getStatusColor(worstStatus) : 'default'}
                                size="medium"
                                sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                            />
                            );
                          })()}
                          </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', verticalAlign: 'top' }}>
                          {(() => {
                            // Normalize itemname function (matches server-side normalization)
                            const normalizeItemname = (str) => {
                              if (!str || str === '') return '';
                              return String(str).trim()
                                .replace(/[^\w\s()]/g, ' ')  // Replace special chars except () with spaces
                                .replace(/\s+/g, ' ')        // Replace multiple spaces with single space
                                .toLowerCase()               // Convert to lowercase
                                .trim();
                            };
                            
                            // Find matching validation summary record for this FRI
                            const itemItemnameNormalized = normalizeItemname(item.itemname || '');
                            const matchingValidation = validationSummaryData.find(vs => {
                              const vsItemnameNormalized = normalizeItemname(vs.itemname || '');
                              return String(vs.foodcourtid || '').trim() === String(item.foodcourtid || '').trim() &&
                                     String(vs.restaurant || '').trim() === String(item.restaurant || '').trim() &&
                                     vsItemnameNormalized === itemItemnameNormalized;
                            });
                            
                            // Get reasons from validation summary (prefer) or item (fallback)
                            const reasons_pre = matchingValidation?.reasons_pre || item.reasons_pre || '';
                            const worstStatus = (() => {
                              const statuses = [
                                sessionModelStatuses.componentStatus,
                                ...(isNewModelFormat ? [] : [sessionModelStatuses.fridStatus, sessionModelStatuses.friwStatus, sessionModelStatuses.bestStatus])
                              ].filter(s => s);
                              return statuses.find(s => s === 'Red') || 
                                              statuses.find(s => s === 'Orange') || 
                                              statuses.find(s => s === 'Green');
                            })();
                            
                            // Format reasons
                            const pastSalesReasons = formatPastSalesReasons(reasons_pre);
                            
                            // If status is Green and no reasons, show "No Performance Issues Detected"
                            if (worstStatus === 'Green' && pastSalesReasons.length === 0) {
                              return (
                                <Box sx={{ color: 'success.main', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                  No Performance Issues Detected
                                </Box>
                              );
                            }
                            
                            return (
                              <Box sx={{ fontSize: '0.8rem' }}>
                                {pastSalesReasons.length > 0 ? (
                                  pastSalesReasons.map((reason, idx) => (
                                    <Box key={idx} sx={{ mb: 1, lineHeight: 1.5 }}>
                                      {reason}
                                    </Box>
                                  ))
                                ) : (
                                  <Box sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                    None
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', verticalAlign: 'top' }}>
                          {(() => {
                            // Normalize itemname function (matches server-side normalization)
                            const normalizeItemname = (str) => {
                              if (!str || str === '') return '';
                              return String(str).trim()
                                .replace(/[^\w\s()]/g, ' ')  // Replace special chars except () with spaces
                                .replace(/\s+/g, ' ')        // Replace multiple spaces with single space
                                .toLowerCase()               // Convert to lowercase
                                .trim();
                            };
                            
                            // Find matching validation summary record for this FRI
                            const itemItemnameNormalized = normalizeItemname(item.itemname || '');
                            const matchingValidation = validationSummaryData.find(vs => {
                              const vsItemnameNormalized = normalizeItemname(vs.itemname || '');
                              return String(vs.foodcourtid || '').trim() === String(item.foodcourtid || '').trim() &&
                                     String(vs.restaurant || '').trim() === String(item.restaurant || '').trim() &&
                                     vsItemnameNormalized === itemItemnameNormalized;
                            });
                            
                            // Get reasons from validation summary (prefer) or item (fallback)
                            const reasons_post = matchingValidation?.reasons_post || item.reasons_post || '';
                            const worstStatus = (() => {
                              const statuses = [
                                sessionModelStatuses.componentStatus,
                                ...(isNewModelFormat ? [] : [sessionModelStatuses.fridStatus, sessionModelStatuses.friwStatus, sessionModelStatuses.bestStatus])
                              ].filter(s => s);
                              return statuses.find(s => s === 'Red') || 
                                     statuses.find(s => s === 'Orange') || 
                                     statuses.find(s => s === 'Green');
                            })();
                            
                            // Get accuracy from validation summary (mape or weighted_mape)
                            // MAPE is stored as decimal (0-1), accuracy = (1 - MAPE) * 100
                            const mape = matchingValidation?.mape || matchingValidation?.weighted_mape || null;
                            
                            // Format reasons (pass accuracy to filter day-of-week flags if >= 90%)
                            const currentWeekAnomalies = formatCurrentWeekAnomalies(reasons_post, mape);
                            
                            // If status is Green and no reasons, show "No Performance Issues Detected"
                            if (worstStatus === 'Green' && currentWeekAnomalies.length === 0) {
                              return (
                                <Box sx={{ color: 'success.main', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                  No Performance Issues Detected
                                </Box>
                              );
                            }
                            
                            return (
                              <Box sx={{ fontSize: '0.8rem' }}>
                                {currentWeekAnomalies.length > 0 ? (
                                  currentWeekAnomalies.map((anomaly, idx) => (
                                    <Box key={idx} sx={{ mb: 1, lineHeight: 1.5 }}>
                                      {anomaly}
                                    </Box>
                                  ))
                                ) : (
                                  <Box sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                    None
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </>
          )}
        </>
      )}
    </Box>
  );
}

export default ItemDetail;

